// src/pages/MinhaFilaDev.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../components/mainLayout";
import { ProjetosApi } from "../service/projetosApi";
import { showError, showInfo } from "../utils/toast";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { api } from "../api";
import "../sass/pages/minhaFilaDev.scss";
import CommentModal from "../components/ui/CommentModal.jsx";
import ProjetoFluxoChamado from "../components/projetos/ProjetoFluxoChamado";

/* ==================== Constantes ==================== */
const STATUS = {
  PENDENTE: "PENDENTE",
  EM_ANDAMENTO: "EM_ANDAMENTO",
  TESTE: "TESTE",
  REAJUSTE: "REAJUSTE",
  TESTADO_APROVADO: "TESTADO_APROVADO",
  TESTADO_REPROVADO: "TESTADO_REPROVADO",
  AGUARDANDO_RETORNO_CLIENTE: "AGUARDANDO_RETORNO_CLIENTE",
  PAUSADO: "PAUSADO",
  FINALIZADO: "FINALIZADO", // ainda em fila
  CONCLUIDO: "CONCLUIDO", // fora da fila (encerrado)
  CANCELADO: "CANCELADO", // fora da fila
};

const STATUS_ALIASES = {
  BACKLOG: "PENDENTE",
  PENDENTE: "PENDENTE",

  EM_ANDAMENTO: "EM_ANDAMENTO",
  ANDAMENTO: "EM_ANDAMENTO",
  EM_PROGRESSO: "EM_ANDAMENTO",
  "EM PROGRESSO": "EM_ANDAMENTO",

  REVISAO: "TESTE",
  REVISÃO: "TESTE",
  PARA_TESTE: "TESTE",
  "PARA TESTE": "TESTE",
  TESTE: "TESTE",

  REAJUSTE: "REAJUSTE",
  "DEVOLVIDO PARA REAJUSTE": "REAJUSTE",
  "DEVOLUÇÃO PARA REAJUSTE": "REAJUSTE",
  "DEVOLUCAO PARA REAJUSTE": "REAJUSTE",
  RETRABALHO: "REAJUSTE",

  TESTADO_APROVADO: "TESTADO_APROVADO",
  "TESTADO APROVADO": "TESTADO_APROVADO",
  "TESTADO - APROVADO": "TESTADO_APROVADO",
  OK_TESTE: "TESTADO_APROVADO",
  "OK TESTE": "TESTADO_APROVADO",

  TESTADO_REPROVADO: "TESTADO_REPROVADO",
  "TESTADO REPROVADO": "TESTADO_REPROVADO",
  "TESTADO - REPROVADO": "TESTADO_REPROVADO",
  FALHA_TESTE: "TESTADO_REPROVADO",
  "FALHA DE TESTE": "TESTADO_REPROVADO",

  AGUARDANDO_RETORNO_CLIENTE: "AGUARDANDO_RETORNO_CLIENTE",
  AGUARDANDO_RETORNO: "AGUARDANDO_RETORNO_CLIENTE",
  "AGUARDANDO RETORNO CLIENTE": "AGUARDANDO_RETORNO_CLIENTE",
  "AGUARDANDO RETORNO DO CLIENTE": "AGUARDANDO_RETORNO_CLIENTE",

  PAUSADO: "PAUSADO",

  // agora CONCLUIDO é canônico, não vira mais FINALIZADO
  CONCLUIDO: "CONCLUIDO",
  CONCLUÍDO: "CONCLUIDO",

  FINALIZADO: "FINALIZADO",
  FINALIZADA: "FINALIZADO",
  FINALIZACAO: "FINALIZADO",
  FINALIZAÇÃO: "FINALIZADO",

  CANCELADO: "CANCELADO",
  CANCELADA: "CANCELADO",
};

const STATUS_LABEL = {
  PENDENTE: "PENDENTE",
  EM_ANDAMENTO: "EM ANDAMENTO",
  TESTE: "PARA TESTE",
  REAJUSTE: "REAJUSTE",
  TESTADO_APROVADO: "TESTADO (APROVADO)",
  TESTADO_REPROVADO: "TESTADO (REPROVADO)",
  AGUARDANDO_RETORNO_CLIENTE: "AGUARDANDO RETORNO DO CLIENTE",
  PAUSADO: "PAUSADO",
  FINALIZADO: "FINALIZADO",
  CONCLUIDO: "CONCLUÍDO",
  CANCELADO: "CANCELADO",
};

const PRIORITY_WEIGHT = { BAIXA: 0, MEDIA: 1, ALTA: 2, CRITICA: 3 };

const STATUS_BLOQUEADOS_PROJETO_CHAMADO = new Set([
  STATUS.FINALIZADO,
  STATUS.CONCLUIDO,
  STATUS.AGUARDANDO_RETORNO_CLIENTE,
]);

const isProjetoOriginadoDeChamado = (projeto) => {
  const id = projeto?.ID_CHAMADO_ORIGEM ?? projeto?.idChamadoOrigem;
  return id !== null && id !== undefined && String(id).trim() !== "";
};

const getStatusOptionsForProject = (projeto, statusAtual = null) => {
  const atual = normalizeStatus(statusAtual);
  const originado = isProjetoOriginadoDeChamado(projeto);

  return Object.values(STATUS).filter((item) => {
    if (!originado) return true;
    if (item === atual) return true;
    return !STATUS_BLOQUEADOS_PROJETO_CHAMADO.has(item);
  });
};

const getSessionValue = (...keys) => {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) ?? localStorage.getItem(key);
    if (value !== null && String(value).trim()) return String(value).trim();
  }
  return "";
};
const SAVED_VIEWS_KEY = "MF_SAVED_VIEWS_V1";
const COLS_KEY = "MF_COLS_V1";
const DENSITY_KEY = "MF_DENSITY_V1";
const SORT_KEY = "MF_SORT_LAST_V1";

/* ==================== Helpers ==================== */
async function postCommentQuick(projectId, texto, autorId) {
  try {
    await api.post(`/projetos/${projectId}/comentarios`, {
      texto,
      autorId: Number(autorId) || undefined,
    });
  } catch {
    showError("Falha ao vincular comentário.");
  }
}
const useDebounce = (v, d = 350) => {
  const [x, setX] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setX(v), d);
    return () => clearTimeout(t);
  }, [v, d]);
  return x;
};
const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "-");
const fmtDateOnly = (v) => (v ? new Date(v).toLocaleDateString() : "-");

const formatDuration = (totalSeconds) => {
  const total = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  return [hours, minutes, seconds]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
};

const createOperationalForm = () => ({
  motivo: "",
  impacto: "",
  previsaoRetorno: "",
  solicitanteId: "",
  idProjetoDestino: "",
  idTarefaDestino: "",
  titulo: "",
  descricao: "",
  prioridade: "CRITICA",
});
const daysDiff = (v) => {
  if (!v) return null;
  const a = new Date(v),
    b = new Date();
  return Math.floor(
    (a - new Date(b.getFullYear(), b.getMonth(), b.getDate())) / 86400000
  );
};
const toIsoWithSeconds = (s) => {
  if (!s) return undefined;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return `${s}:00`;
  const d = new Date(s);
  if (isNaN(d)) return undefined;
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours()
  )}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours()
  )}:${p(d.getMinutes())}`;
};

const getInitialDevId = () =>
  sessionStorage.getItem("IdFuncionario") ||
  sessionStorage.getItem("Idfuncionario") ||
  localStorage.getItem("IdFuncionario") ||
  localStorage.getItem("Idfuncionario") ||
  sessionStorage.getItem("DevId") ||
  localStorage.getItem("DevId") ||
  "";

/* -------- nomes -------- */
const pickName = (obj) =>
  obj?.NOME ??
  obj?.Nome ??
  obj?.nome ??
  obj?.NOME_DEV ??
  obj?.DEV_NOME ??
  obj?.NM_DEV ??
  obj?.NOME_FUNCIONARIO ??
  obj?.FUNCIONARIO_NOME ??
  obj?.NM_FUNCIONARIO ??
  obj?.NOME_FUN ??
  obj?.NOME_COMPLETO ??
  obj?.NM_COMPLETO ??
  obj?.NOME_USUARIO ??
  obj?.NM_USUARIO ??
  obj?.USUARIO_NOME ??
  obj?.AUTOR_NOME ??
  obj?.CRIADOR_NOME ??
  obj?.CRIADO_POR ??
  obj?.RESPONSAVEL_NOME ??
  obj?.PARTICIPANTE_NOME ??
  obj?.NM_RAZAO_SOCIAL ??
  obj?.RAZAO_SOCIAL ??
  obj?.Usuario ??
  obj?.USUARIO ??
  null;

function normalizeStatus(s) {
  if (!s) return null;
  const up = String(s).trim().toUpperCase();
  return STATUS_ALIASES[up] ?? (STATUS[up] ? up : null);
}

const normalizeBatchNames = (payload) => {
  if (!payload) return {};

  const root =
    payload?.data?.data ??
    payload?.data?.items ??
    payload?.data?.list ??
    payload?.data ??
    payload?.items ??
    payload?.list ??
    payload;

  if (root?.map && typeof root.map === "object" && !Array.isArray(root.map)) {
    const out = {};
    for (const [key, value] of Object.entries(root.map)) {
      const id = Number(key);
      const name =
        typeof value === "string"
          ? value
          : pickName(value) ?? value?.nome ?? value?.Nome;
      if (id > 0 && name) out[id] = String(name);
    }
    return out;
  }

  if (Array.isArray(root)) {
    const out = {};
    root.forEach((item) => {
      const id = Number(
        item?.IdFuncionario ??
          item?.ID_FUNCIONARIO ??
          item?.ID_DEV ??
          item?.ID ??
          item?.id
      );
      const name = pickName(item);
      if (id > 0 && name) out[id] = String(name);
    });
    return out;
  }

  if (typeof root === "object") {
    const directId = Number(
      root?.IdFuncionario ??
        root?.ID_FUNCIONARIO ??
        root?.ID_DEV ??
        root?.ID ??
        root?.id
    );
    const directName = pickName(root);

    if (directId > 0 && directName) {
      return { [directId]: String(directName) };
    }

    const out = {};
    for (const [key, value] of Object.entries(root)) {
      const id = Number(key);
      const name =
        typeof value === "string" ? value : pickName(value);
      if (id > 0 && name) out[id] = String(name);
    }
    return out;
  }

  return {};
};

/* ==================== UI ==================== */
const StatusBadge = ({ value }) => {
  const v = normalizeStatus(value) || "PENDENTE";
  const map = {
    PENDENTE: "badge-gray",
    EM_ANDAMENTO: "badge-amber",
    TESTE: "badge-blue",
    REAJUSTE: "badge-purple",
    TESTADO_APROVADO: "badge-emerald",
    TESTADO_REPROVADO: "badge-rose",
    AGUARDANDO_RETORNO_CLIENTE: "badge-gold",
    PAUSADO: "badge-slate",
    FINALIZADO: "badge-green",
    CONCLUIDO: "badge-green", // por exemplo
    CANCELADO: "badge-red",
  };

  return (
    <span
      className={`badge ${map[v] || "badge-gray"} badge--truncate`}
      title={STATUS_LABEL[v]}
    >
      {STATUS_LABEL[v]}
    </span>
  );
};

const PriorityPill = ({ value }) => {
  const map = {
    BAIXA: "pill-gray",
    MEDIA: "pill-sky",
    ALTA: "pill-orange",
    CRITICA: "pill-rose",
  };
  return <span className={`pill ${map[value] || "pill-gray"}`}>{value}</span>;
};

function PanelModal({
  open,
  title,
  onClose,
  children,
  size = "lg",
}) {
  const firstFieldRef = useRef(null);
  const onCloseRef = useRef(onClose);

  /*
   * Mantém a função de fechamento atualizada sem reiniciar o efeito do modal.
   * Funções declaradas dentro da tela ganham uma nova referência a cada
   * digitação; depender diretamente de onClose fazia o foco voltar ao primeiro
   * campo sempre que o formulário era atualizado.
   */
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const requestClose = useCallback(() => {
    onCloseRef.current?.();
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const onKey = (event) => {
      if (event.key === "Escape") requestClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);

    /*
     * O foco inicial é aplicado somente quando o modal abre. Alterações nos
     * campos não executam este efeito novamente.
     */
    const focusTimer = window.setTimeout(() => {
      const field = firstFieldRef.current;

      if (field && !field.disabled) {
        field.focus({ preventScroll: true });
      }
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, requestClose]);

  if (!open) return null;

  return (
    <div
      className="queue-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <section
        className={`queue-modal queue-modal-${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="queue-modal-header">
          <div>
            <span className="queue-modal-eyebrow">Workspace do projeto</span>
            <h2>{title}</h2>
          </div>

          <Button
            type="button"
            variant="ghost"
            onClick={requestClose}
            aria-label="Fechar modal"
          >
            Fechar
          </Button>
        </header>

        <div className="queue-modal-content">
          {typeof children === "function"
            ? children({ firstFieldRef })
            : children}
        </div>
      </section>
    </div>
  );
}

const HL = ({ text, q }) => {
  if (!q) return <>{text}</>;
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = String(text).split(new RegExp(`(${esc(q)})`, "ig"));
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase() ? (
          <mark key={i}>{p}</mark>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  );
};

/* ========= Sort (clicando no cabeçalho) ========= */
const DEFAULT_DIRS = {
  RANK: "asc",
  ID_PROJETO: "asc",
  CODIGO: "asc",
  TITULO: "asc",
  CRIADOR: "asc",
  CREATED_AT: "desc",
  FOCUS_ATIVO: "desc",
  SCORE: "desc",
  STATUS: "asc",
  PRIORIDADE: "desc",
  PROGRESSO: "desc",
  DT_PREVISTA: "asc",
};

function SortableTh({ active, dir, onClick, children, style }) {
  const arrow = active ? (dir === "asc" ? "▲" : "▼") : "⇅";
  return (
    <th
      onClick={onClick}
      style={{ cursor: "pointer", userSelect: "none", ...(style || {}) }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {children}
        <span className="sort-indicator" aria-hidden>
          {arrow}
        </span>
      </span>
    </th>
  );
}

function AccordionSection({
  id,
  title,
  subtitle,
  open,
  badge,
  children,
}) {
  if (!open) return null;

  return (
    <section
      className="workspace-tab-panel"
      role="tabpanel"
      id={`workspace-panel-${id}`}
      aria-labelledby={`workspace-tab-${id}`}
    >
      <div className="workspace-tab-panel-heading">
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>

        {badge && (
          <span className="workspace-tab-panel-badge">
            {badge}
          </span>
        )}
      </div>

      <div className="workspace-tab-panel-content">
        {children}
      </div>
    </section>
  );
}

/* ==================== Tela ==================== */
export default function MinhaFilaDev() {
  const [devId, setDevId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState("");
  const qDeb = useDebounce(q, 250);
  const [statusFilter, setStatusFilter] = useState("");
  const [statusAliasText, setStatusAliasText] = useState("");
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [showFocusOnly, setShowFocusOnly] = useState(false);
  const [includeFinished, setIncludeFinished] = useState(false);
  const [manageDetail, setManageDetail] = useState(null);

  /* -------- Controle operacional de trabalho -------- */
  const [atividadeAtual, setAtividadeAtual] = useState(null);
  const [clockNow, setClockNow] = useState(Date.now());
  const [operationalMode, setOperationalMode] = useState(null);
  const [operationalSaving, setOperationalSaving] = useState(false);
  const [operationalForm, setOperationalForm] = useState(
    createOperationalForm
  );
  const [destinationTasks, setDestinationTasks] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);
  const [projectParticipants, setProjectParticipants] = useState([]);

  const [commentAsk, setCommentAsk] = useState({
    open: false,
    title: "",
    subtitle: "",
    onConfirm: null,
  });

  const openCommentModal = (title, subtitle, onConfirm) =>
    setCommentAsk({ open: true, title, subtitle, onConfirm });

  const closeCommentModal = () =>
    setCommentAsk({ open: false, title: "", subtitle: "", onConfirm: null });

  useEffect(() => {
    if (!atividadeAtual?.ID_SESSAO) return undefined;

    setClockNow(Date.now());
    const timer = window.setInterval(() => setClockNow(Date.now()), 1000);

    return () => window.clearInterval(timer);
  }, [atividadeAtual?.ID_SESSAO]);

  // <<< inicia com sort salvo (ou padrão)
  const [sort, setSort] = useState(
    () => localStorage.getItem(SORT_KEY) || "RANK:asc"
  );
  useEffect(() => {
    localStorage.setItem(SORT_KEY, sort);
  }, [sort]);

  const [density, setDensity] = useState(
    () => localStorage.getItem(DENSITY_KEY) || "conforto"
  );
  const [cols, setCols] = useState(() => {
    try {
      return (
        JSON.parse(localStorage.getItem(COLS_KEY)) || {
          pos: true,
          id: true,
          codigo: true,
          titulo: true,
          criador: true,
          foco: true,
          score: true,
          status: true,
          prioridade: true,
          progresso: true,
          prevista: true,
          acoes: true,
        }
      );
    } catch {
      return {
        pos: true,
        id: true,
        codigo: true,
        titulo: true,
        criador: true,
        foco: true,
        score: true,
        status: true,
        prioridade: true,
        progresso: true,
        prevista: true,
        acoes: true,
      };
    }
  });
  useEffect(() => localStorage.setItem(COLS_KEY, JSON.stringify(cols)), [cols]);
  useEffect(() => localStorage.setItem(DENSITY_KEY, density), [density]);

  const [views, setViews] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SAVED_VIEWS_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [activeView, setActiveView] = useState("");

  const saveView = () => {
    const name = window.prompt("Nome da visão:");
    if (!name) return;
    const v = {
      id: String(Date.now()),
      name,
      query: q,
      statusFilter,
      showOverdueOnly,
      showFocusOnly,
      includeFinished,
      sort,
      cols,
      density,
    };
    const arr = [...views, v];
    setViews(arr);
    localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(arr));
    setActiveView(v.id);
    showInfo("Visão salva.");
  };
  const loadView = (id) => {
    const v = views.find((x) => x.id === id);
    if (!v) return;
    setQ(v.query || "");
    setStatusFilter(v.statusFilter || "");
    setStatusAliasText("");
    setShowOverdueOnly(!!v.showOverdueOnly);
    setShowFocusOnly(!!v.showFocusOnly);
    setIncludeFinished(!!v.includeFinished);
    setSort(v.sort || "RANK:asc");
    setCols(v.cols || cols);
    setDensity(v.density || "conforto");
    setActiveView(id);
  };
  const deleteView = () => {
    if (!activeView) return;
    if (!window.confirm("Remover visão selecionada?")) return;
    const arr = views.filter((x) => x.id !== activeView);
    setViews(arr);
    localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(arr));
    setActiveView("");
    showInfo("Visão removida.");
  };
  const resetLayout = () => {
    setSort("RANK:asc");
    setStatusFilter("");
    setStatusAliasText("");
    setShowOverdueOnly(false);
    setShowFocusOnly(false);
    setIncludeFinished(false);
    setQ("");
    setDensity("conforto");
    setCols({
      pos: true,
      id: true,
      codigo: true,
      titulo: true,
      criador: true,
      foco: true,
      score: true,
      status: true,
      prioridade: true,
      progresso: true,
      prevista: true,
      acoes: true,
    });
    setActiveView("");
    showInfo("Layout resetado.");
  };

  // nomes
  const [devNames, setDevNames] = useState({});
  const getDevName = useCallback(
    (personOrId) => {
      const directName =
        personOrId && typeof personOrId === "object"
          ? pickName(personOrId)
          : null;

      if (directName) return String(directName);

      const id =
        personOrId && typeof personOrId === "object"
          ? Number(
              personOrId.ID_DEV ??
                personOrId.RESPONSAVEL_ID ??
                personOrId.AUTOR_ID ??
                personOrId.CREATED_BY ??
                personOrId.CRIADOR_ID ??
                personOrId.IdFuncionario ??
                personOrId.ID_FUNCIONARIO ??
                personOrId.ID ??
                personOrId.id
            )
          : Number(personOrId);

      if (id > 0 && devNames[id]) return devNames[id];
      return id > 0 ? `Técnico #${id}` : "Não identificado";
    },
    [devNames],
  );
  const mergeNames = (obj) => {
    if (!obj) return;

    setDevNames((previous) => {
      const accepted = Object.entries(obj).filter(([id, name]) => {
        if (!id || !name) return false;

        const current = String(previous[id] || "").trim();
        return (
          !current ||
          /^#\d+$/.test(current) ||
          /^Técnico #\d+$/i.test(current) ||
          /^Não identificado$/i.test(current)
        );
      });

      if (!accepted.length) return previous;

      return {
        ...previous,
        ...Object.fromEntries(accepted),
      };
    });
  };

  /* -------- Busca de nomes -------- */
  const NAME_ENDPOINTS = ["/func/names"];
  const tryBatch = async (ids) => {
    for (const base of NAME_ENDPOINTS) {
      try {
        const { data } = await api.get(base, {
          params: { ids: ids.join(",") },
        });
        const map = normalizeBatchNames(data);
        if (Object.keys(map).length) return map;
      } catch {}
    }
    return {};
  };
  const tryOneByOne = async (ids) => {
    const out = {};
    await Promise.all(
      ids.map(async (id) => {
        try {
          const { data } = await api.get(`/func/${id}`);
          const candidate =
            data?.data?.data ??
            data?.data ??
            data?.funcionario ??
            data?.usuario ??
            data;
          const name = pickName(candidate);
          if (name) out[id] = String(name);
        } catch {}
      })
    );
    return out;
  };
  const ensureDevNames = async (idsRaw) => {
    const ids = Array.from(
      new Set((idsRaw || []).map((x) => Number(x)).filter(Boolean))
    ).filter((id) => !devNames[id]);
    if (!ids.length) return;
    let map = await tryBatch(ids);
    if (!Object.keys(map).length) map = await tryOneByOne(ids);
    if (Object.keys(map).length) mergeNames(map);
  };

  /* -------- Creator helpers -------- */
  const pickCreator = (r = {}) => {
    const id =
      Number(
        r.CRIADO_POR_ID ??
          r.CRIADOR_ID ??
          r.ID_CRIADOR ??
          r.AUTOR_ID ??
          r.ID_AUTOR ??
          r.CREATED_BY ??
          r.ID_USUARIO_CRIACAO ??
          (typeof r.CRIADOR === "object"
            ? r.CRIADOR.ID ?? r.CRIADOR.Id ?? r.CRIADOR.IdFuncionario
            : undefined)
      ) || null;

    const nome =
      r.CRIADO_POR ??
      r.NOME_CRIADOR ??
      r.CRIADOR_NOME ??
      r.AUTOR ??
      r.AUTOR_NOME ??
      (typeof r.CRIADOR === "object"
        ? pickName(r.CRIADOR) ?? r.CRIADOR.nome ?? r.CRIADOR.Nome
        : undefined) ??
      null;

    return { id, nome };
  };

  const normalizeQueue = (rawList) => {
    const list = Array.isArray(rawList) ? rawList : [];
    const namesToMerge = {};

    const out = list.map((r, i) => {
      const proj =
        r.PROJETO ??
        r.projeto ??
        r.PROJECT ??
        r.project ??
        r.PROJ ??
        r.proj ??
        r;

      // PROJETO sobrescreve os campos nulos do wrapper
      const merged = { ...r, ...(proj || {}) };

      const st = normalizeStatus(
        merged.STATUS ??
          merged.status ??
          merged.STATUS_ATUAL ??
          merged.statusAtual ??
          merged.SITUACAO ??
          merged.situacao
      );

      const { id: creatorId, nome: creatorName } = pickCreator(merged);
      if (creatorId && creatorName)
        namesToMerge[creatorId] = String(creatorName);

      return {
        POS:
          merged.POS ?? merged.pos ?? merged.POSICAO ?? merged.posicao ?? i + 1,

        ID_PROJETO:
          merged.ID_PROJETO ??
          merged.IdProjeto ??
          merged.idProjeto ??
          merged.ID ??
          merged.id ??
          null,

        CODIGO:
          merged.CODIGO ??
          merged.codigo ??
          merged.CODIGO_PROJETO ??
          merged.codigoProjeto ??
          "",

        TITULO:
          merged.TITULO ??
          merged.titulo ??
          merged.TITULO_PROJETO ??
          merged.tituloProjeto ??
          "",

        ID_CHAMADO_ORIGEM:
          merged.ID_CHAMADO_ORIGEM ??
          merged.idChamadoOrigem ??
          merged.ID_CHAMADO ??
          merged.idChamado ??
          null,

        STATUS: st || STATUS.PENDENTE,

        PRIORIDADE:
          merged.PRIORIDADE ??
          merged.prioridade ??
          merged.Prioridade ??
          "MEDIA",

        PROGRESSO:
          Number(
            merged.PROGRESSO ??
              merged.progresso ??
              merged.Progresso ??
              merged.PERCENTUAL ??
              merged.percentual ??
              0
          ) || 0,

        DT_PREVISTA:
          merged.DT_PREVISTA ??
          merged.dtPrevista ??
          merged.DATA_PREVISTA ??
          merged.dataPrevista ??
          null,

        SCORE:
          Number(
            merged.SCORE ??
              merged.Score ??
              merged.rankScore ??
              merged.RANK ??
              merged.rank
          ) || NaN,

        FOCUS_ATIVO: !!(
          merged.FOCUS_ATIVO ??
          merged.FOCUS ??
          merged.EM_FOCO ??
          merged.emFoco ??
          false
        ),

        CRIADOR_ID: creatorId,
        CREATED_AT:
          merged.CRIADO_EM ??
          merged.criadoEm ??
          merged.CREATED_AT ??
          merged.createdAt ??
          merged.created_at ??
          null,
      };
    });

    if (Object.keys(namesToMerge).length) mergeNames(namesToMerge);
    return out;
  };

  // Enriquecimento: buscar criador via detalhe quando faltou
  const enrichCreators = async (list) => {
    const missing = list
      .filter((x) => !x.CRIADOR_ID || !x.CREATED_AT)
      .map((x) => x.ID_PROJETO);
    if (!missing.length) return;

    const pool = 4;
    let idx = 0;
    const localNames = {};
    const updates = {};

    const worker = async () => {
      while (idx < missing.length) {
        const id = missing[idx++];
        try {
          const { data } = ProjetosApi?.get
            ? await ProjetosApi.get(id)
            : await api.get(`/projetos/${id}`);
          const { id: creatorId, nome: creatorName } = pickCreator(data || {});
          const createdAt =
            data?.CREATED_AT ?? data?.createdAt ?? data?.created_at ?? null;

          if (creatorId || createdAt) {
            updates[id] = {
              ...(creatorId ? { CRIADOR_ID: creatorId } : {}),
              ...(createdAt ? { CREATED_AT: createdAt } : {}),
            };
          }
          if (creatorId && creatorName)
            localNames[creatorId] = String(creatorName);
        } catch {}
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(pool, missing.length) }, worker)
    );

    if (Object.keys(updates).length) {
      setRows((prev) =>
        prev.map((r) =>
          updates[r.ID_PROJETO] ? { ...r, ...updates[r.ID_PROJETO] } : r
        )
      );
      const ids = Object.values(updates)
        .map((u) => u.CRIADOR_ID)
        .filter(Boolean);
      if (ids.length) await ensureDevNames(ids);
    }
    if (Object.keys(localNames).length) mergeNames(localNames);
  };

  /* -------- Operação atual / sessões -------- */

  const carregarAtividadeAtual = async (id, silent = false) => {
    const idNum = Number(id) || 0;
    if (!idNum) {
      setAtividadeAtual(null);
      return null;
    }

    try {
      const { data } = await ProjetosApi.obterAtividadeAtual(idNum);
      const sessao = data?.sessao || null;
      setAtividadeAtual(sessao);
      setClockNow(Date.now());
      return sessao;
    } catch (e) {
      if (!silent) {
        showError(
          e?.response?.data?.message || "Falha ao consultar atividade atual."
        );
      }
      return null;
    }
  };

  const loadDestinationTasks = async (idProjeto) => {
    const id = Number(idProjeto) || 0;
    if (!id) {
      setDestinationTasks([]);
      return;
    }

    try {
      const { data } = await ProjetosApi.listarTarefas(id);
      setDestinationTasks(Array.isArray(data) ? data : data?.data || []);
    } catch {
      setDestinationTasks([]);
    }
  };

  const openOperational = (mode, seed = {}) => {
    const next = {
      ...createOperationalForm(),
      ...seed,
    };

    setOperationalForm(next);
    setOperationalMode(mode);
    setDestinationTasks([]);

    if (mode === "TROCAR" && next.idProjetoDestino) {
      loadDestinationTasks(next.idProjetoDestino);
    }
  };

  const closeOperational = (force = false) => {
    if (operationalSaving && !force) return;
    setOperationalMode(null);
    setOperationalForm(createOperationalForm());
    setDestinationTasks([]);
  };

  const atualizarAposOperacao = async (idProjetoAbrir = null) => {
    await Promise.all([
      carregarAtividadeAtual(devId, true),
      loadRef.current ? loadRef.current(devId) : Promise.resolve(),
    ]);

    if (idProjetoAbrir) {
      await openManage(idProjetoAbrir);
    }
  };

  const iniciarAtividadeProjeto = async (idProjeto, idTarefa = null) => {
    const projetoId = Number(idProjeto) || 0;
    const tarefaId = Number(idTarefa) || null;
    const devIdNum = Number(devId) || 0;

    if (!projetoId || !devIdNum) return;

    if (atividadeAtual?.ID_SESSAO) {
      const mesmaAtividade =
        Number(atividadeAtual.ID_PROJETO) === projetoId &&
        Number(atividadeAtual.ID_TAREFA || 0) === Number(tarefaId || 0);

      if (mesmaAtividade) {
        showInfo("Esta atividade já está em andamento.");
        return;
      }

      openOperational("TROCAR", {
        idProjetoDestino: String(projetoId),
        idTarefaDestino: tarefaId ? String(tarefaId) : "",
        motivo: "",
      });
      return;
    }

    setOperationalSaving(true);
    try {
      const { data } = await ProjetosApi.iniciarTrabalho({
        devId: devIdNum,
        idProjeto: projetoId,
        idTarefa: tarefaId,
        observacao: "Iniciado pela tela Minha Fila",
        autorId: devIdNum,
      });

      setAtividadeAtual(data?.sessao || null);
      setRows((prev) =>
        prev.map((item) => ({
          ...item,
          STATUS:
            item.ID_PROJETO === projetoId
              ? STATUS.EM_ANDAMENTO
              : item.STATUS,
          FOCUS_ATIVO: item.ID_PROJETO === projetoId,
        }))
      );

      setManageDetail((previous) =>
        Number(previous?.ID_PROJETO) === projetoId
          ? {
              ...previous,
              STATUS: STATUS.EM_ANDAMENTO,
              QT_SESSOES_ATIVAS: 1,
            }
          : previous
      );
      setMf((previous) => ({
        ...previous,
        status: STATUS.EM_ANDAMENTO,
      }));

      showInfo(
        data?.alreadyActive
          ? "Esta atividade já estava em andamento."
          : "Trabalho iniciado. O cronômetro já está contabilizando o tempo."
      );
    } catch (e) {
      showError(
        e?.response?.data?.message || "Falha ao iniciar a atividade."
      );
    } finally {
      setOperationalSaving(false);
    }
  };

  const confirmarPausa = async () => {
    const motivo = String(operationalForm.motivo || "").trim();
    const devIdNum = Number(devId) || 0;

    if (!atividadeAtual?.ID_SESSAO) {
      showError("Nenhuma atividade está em andamento.");
      return;
    }

    if (!motivo) {
      showError("Informe o motivo da pausa.");
      return;
    }

    setOperationalSaving(true);
    try {
      await ProjetosApi.pararTrabalho({
        devId: devIdNum,
        idSessao: atividadeAtual.ID_SESSAO,
        motivoEncerramento: "PAUSA_MANUAL",
        observacao: motivo,
        statusSessao: "ENCERRADA",
        pausarAtividade: true,
        autorId: devIdNum,
      });

      const projetoOrigem = atividadeAtual.ID_PROJETO;
      setAtividadeAtual(null);
      setRows((prev) =>
        prev.map((item) =>
          item.ID_PROJETO === projetoOrigem
            ? { ...item, STATUS: STATUS.PAUSADO, FOCUS_ATIVO: false }
            : item
        )
      );
      setManageDetail((previous) =>
        Number(previous?.ID_PROJETO) === Number(projetoOrigem)
          ? {
              ...previous,
              STATUS: STATUS.PAUSADO,
              sessoesAtivas: [],
              QT_SESSOES_ATIVAS: 0,
            }
          : previous
      );
      setMf((previous) => ({
        ...previous,
        status: STATUS.PAUSADO,
      }));

      closeOperational(true);
      showInfo("Atividade pausada. O cronômetro foi encerrado e o histórico foi preservado.");
      await atualizarAposOperacao();
    } catch (e) {
      showError(e?.response?.data?.message || "Falha ao pausar atividade.");
    } finally {
      setOperationalSaving(false);
    }
  };

  const confirmarTroca = async () => {
    const devIdNum = Number(devId) || 0;
    const projetoDestino = Number(operationalForm.idProjetoDestino) || 0;
    const tarefaDestino = Number(operationalForm.idTarefaDestino) || null;
    const motivo = String(operationalForm.motivo || "").trim();

    if (!atividadeAtual?.ID_SESSAO) {
      showError("Não existe atividade atual para ser trocada.");
      return;
    }

    if (!projetoDestino) {
      showError("Selecione o projeto que será iniciado.");
      return;
    }

    if (!motivo) {
      showError("Informe o motivo da interrupção.");
      return;
    }

    setOperationalSaving(true);
    try {
      const { data } = await ProjetosApi.trocarAtividade({
        devId: devIdNum,
        idProjetoDestino: projetoDestino,
        idTarefaDestino: tarefaDestino,
        tipoInterrupcao: "ALTERACAO_PRIORIDADE",
        solicitanteId: Number(operationalForm.solicitanteId) || null,
        motivo,
        impacto: operationalForm.impacto || null,
        previsaoRetorno: operationalForm.previsaoRetorno || null,
        observacaoDestino: motivo,
        autorId: devIdNum,
      });

      setAtividadeAtual(data?.destino || null);
      closeOperational(true);
      showInfo("Atividade trocada. O tempo anterior foi encerrado e a interrupção foi registrada.");
      await atualizarAposOperacao(projetoDestino);
    } catch (e) {
      showError(
        e?.response?.data?.message || "Falha ao trocar de atividade."
      );
    } finally {
      setOperationalSaving(false);
    }
  };

  const confirmarIncidente = async () => {
    const devIdNum = Number(devId) || 0;
    const titulo = String(operationalForm.titulo || "").trim();
    const descricao = String(operationalForm.descricao || "").trim();

    if (!titulo) {
      showError("Informe o título do incidente.");
      return;
    }

    if (!descricao) {
      showError("Informe o problema que precisa ser atendido.");
      return;
    }

    setOperationalSaving(true);
    try {
      const { data } = await ProjetosApi.criarIncidenteRapido({
        devId: devIdNum,
        titulo,
        descricao,
        impacto: operationalForm.impacto || null,
        prioridade: operationalForm.prioridade || "CRITICA",
        solicitanteId: Number(operationalForm.solicitanteId) || null,
        tipoInterrupcao: "ATENDIMENTO_URGENTE",
        autorId: devIdNum,
      });

      setAtividadeAtual(data?.sessao || null);
      closeOperational(true);
      showInfo(
        "Incidente provisório criado. A atividade anterior foi interrompida e o novo cronômetro foi iniciado."
      );
      await atualizarAposOperacao(data?.projeto?.ID_PROJETO || null);
    } catch (e) {
      showError(
        e?.response?.data?.message || "Falha ao registrar incidente urgente."
      );
    } finally {
      setOperationalSaving(false);
    }
  };

  /* -------- Load -------- */

  const load = async (id) => {
    if (!id) return;
    setLoading(true);
    const ctrl = new AbortController();
    const { signal } = ctrl;
    try {
      let resp;
      try {
        resp = await api.get(`/projetos/devs/${Number(id)}/fila`, {
          params: { incluirFinalizados: 1, incluirCancelados: 0 },
          signal,
        });
      } catch {
        resp = await api.get("/projetos/me", {
          params: { devId: Number(id) },
          signal,
        });
      }
      if (signal.aborted) return;

      const data = Array.isArray(resp?.data)
        ? resp.data
        : resp?.data?.data || [];
      const list = normalizeQueue(data);

      setRows((prev) => {
        const keep = new Map();
        // mantém FINALIZADO já visível (sem CANCELADO por padrão)
        prev
          .filter((p) => [STATUS.FINALIZADO].includes(p.STATUS))
          .forEach((p) => keep.set(p.ID_PROJETO, p));
        list.forEach((n) => keep.set(n.ID_PROJETO, n));
        return Array.from(keep.values());
      });

      ensureDevNames(list.map((x) => x.CRIADOR_ID)).catch(() => {});
      enrichCreators(list).catch(() => {});
      await carregarAtividadeAtual(id, true);
    } catch (e) {
      if (!/AbortError/i.test(String(e?.name || e)))
        showError("Falha ao carregar sua fila.");
    } finally {
      setLoading(false);
    }
  };

/*
 * Mantém sempre a versão mais recente de load sem criar um efeito
 * dependente de uma função recriada a cada render.
 */
const loadRef = useRef(null);
loadRef.current = load;

  useEffect(() => {
    const id = getInitialDevId();
    if (!id) {
      showError("Sessão sem IdFuncionario. Faça login novamente.");
      return;
    }
    setDevId(String(id));
    sessionStorage.setItem("IdFuncionario", String(id));
    sessionStorage.setItem("DevId", String(id));
    loadRef.current(String(id));
  }, []);

  /* -------- Derivados -------- */
  useEffect(() => {
    if (!statusAliasText.trim()) return;
    const canon = normalizeStatus(statusAliasText);
    if (canon) {
      setStatusFilter(canon);
      setStatusAliasText(""); // <-- limpa após aplicar
    }
  }, [statusAliasText]);

  const countsByStatus = useMemo(() => {
    const base = includeFinished
      ? rows
      : rows.filter(
          (r) =>
            r.STATUS !== STATUS.CANCELADO &&
            r.STATUS !== STATUS.CONCLUIDO &&
            r.STATUS !== STATUS.AGUARDANDO_RETORNO_CLIENTE,
        );
    const map = Object.fromEntries(Object.values(STATUS).map((s) => [s, 0]));
    base.forEach((r) => {
      const v = normalizeStatus(r.STATUS);
      if (v) map[v] = (map[v] || 0) + 1;
    });
    return map;
  }, [rows, includeFinished]);

  const overdueTotal = useMemo(
    () =>
      rows.filter((r) => {
        const d = daysDiff(r.DT_PREVISTA);
        return (
          d !== null &&
          d < 0 &&
          r.STATUS !== STATUS.FINALIZADO &&
          r.STATUS !== STATUS.CANCELADO &&
          r.STATUS !== STATUS.CONCLUIDO &&
          r.STATUS !== STATUS.AGUARDANDO_RETORNO_CLIENTE
        );
      }).length,
    [rows]
  );

  // força Content-Disposition no Supabase (ou qualquer URL)
  function buildDownloadUrl(u, name = "") {
    if (!u) return "#";
    try {
      const url = new URL(u, window.location.origin);
      if (!url.searchParams.has("download")) {
        url.searchParams.set("download", name || "arquivo");
      }
      return url.toString();
    } catch {
      const sep = u.includes("?") ? "&" : "?";
      return `${u}${sep}download=${encodeURIComponent(name || "arquivo")}`;
    }
  }

  async function downloadAttachment(detail, anexo) {
    const name = anexo?.name || "arquivo";
    const primaryUrl = anexo?.downloadUrl || anexo?.url;
    if (!primaryUrl) return;

    try {
      const publicUrl = buildDownloadUrl(primaryUrl, name);
      const res = await fetch(publicUrl, { mode: "cors", credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      if (blob.type && blob.type.includes("text/html")) {
        throw new Error("HTML error page");
      }
      saveBlob(blob, name);
      return;
    } catch (e) {}

    try {
      const { data } = await api.get(
        `/projetos/${detail.ID_PROJETO}/anexos/${anexo.id}/link`,
        { params: { filename: name } }
      );
      if (data?.url) {
        const signed = buildDownloadUrl(data.url, name);
        const res = await fetch(signed, { mode: "cors", credentials: "omit" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (blob.type && blob.type.includes("text/html")) {
          throw new Error("HTML error page (signed)");
        }
        saveBlob(blob, name);
        return;
      }
    } catch (e) {
      try {
        const resp = await api.get(
          `/projetos/${detail.ID_PROJETO}/anexos/${anexo.id}/download`,
          { responseType: "blob" }
        );
        saveBlob(resp.data, name);
        return;
      } catch {}
    }

    showError(
      "Não foi possível baixar o anexo. Verifique se o link é público ou peça um link assinado."
    );
  }

  // Salva um Blob como arquivo no navegador
  function saveBlob(blob, filename = "arquivo") {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "arquivo";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 0);
  }

  const view = useMemo(() => {
    let list = [...rows];
    if (!includeFinished)
      list = list.filter(
        (r) =>
          r.STATUS !== STATUS.CANCELADO &&
          r.STATUS !== STATUS.CONCLUIDO &&
          r.STATUS !== STATUS.AGUARDANDO_RETORNO_CLIENTE,
      );
    if (statusFilter) list = list.filter((r) => r.STATUS === statusFilter);
    if (showFocusOnly) list = list.filter((r) => r.FOCUS_ATIVO);

    if (qDeb && qDeb.trim()) {
      const raw = qDeb.trim().toLowerCase();
      const s = raw.startsWith("#") ? raw.slice(1) : raw;
      list = list.filter((r) => {
        const idTxt = String(r.ID_PROJETO || "");
        const titulo = String(r.TITULO || "").toLowerCase();
        const criador = String(getDevName(r.CRIADOR_ID) || "").toLowerCase();

        return idTxt.includes(s) || titulo.includes(s) || criador.includes(s);
      });
    }

    if (showOverdueOnly) {
      list = list.filter((r) => {
        const d = daysDiff(r.DT_PREVISTA);
        return (
          d !== null &&
          d < 0 &&
          r.STATUS !== STATUS.FINALIZADO &&
          r.STATUS !== STATUS.CANCELADO &&
          r.STATUS !== STATUS.CONCLUIDO &&
          r.STATUS !== STATUS.AGUARDANDO_RETORNO_CLIENTE
        );
      });
    }

    const [key, dir] = String(sort).split(":");
    const mul = dir === "desc" ? -1 : 1;

    if (key !== "RANK") {
      list.sort((a, b) => {
        switch (key) {
          case "ID_PROJETO":
            return ((a.ID_PROJETO || 0) - (b.ID_PROJETO || 0)) * mul;
          case "CODIGO":
            return (
              String(a.CODIGO || "").localeCompare(String(b.CODIGO || "")) * mul
            );
          case "TITULO":
            return (
              String(a.TITULO || "").localeCompare(String(b.TITULO || "")) * mul
            );
          case "CRIADOR":
            return (
              String(getDevName(a.CRIADOR_ID) || "").localeCompare(
                String(getDevName(b.CRIADOR_ID) || "")
              ) * mul
            );
          case "FOCUS_ATIVO":
            return ((a.FOCUS_ATIVO ? 1 : 0) - (b.FOCUS_ATIVO ? 1 : 0)) * mul;
          case "SCORE": {
            const av = Number.isFinite(a.SCORE) ? a.SCORE : -Infinity;
            const bv = Number.isFinite(b.SCORE) ? b.SCORE : -Infinity;
            return (av - bv) * mul;
          }
          case "STATUS":
            return (
              String(STATUS_LABEL[a.STATUS] || a.STATUS || "").localeCompare(
                String(STATUS_LABEL[b.STATUS] || b.STATUS || "")
              ) * mul
            );
          case "PRIORIDADE": {
            const av = PRIORITY_WEIGHT[a.PRIORIDADE] ?? -1;
            const bv = PRIORITY_WEIGHT[b.PRIORIDADE] ?? -1;
            return (av - bv) * mul;
          }
          case "PROGRESSO":
            return (
              ((Number(a.PROGRESSO) || 0) - (Number(b.PROGRESSO) || 0)) * mul
            );
          case "DT_PREVISTA":
            return (
              (new Date(a.DT_PREVISTA || 0) - new Date(b.DT_PREVISTA || 0)) *
              mul
            );
          case "CREATED_AT":
            return (
              (new Date(a.CREATED_AT || 0) - new Date(b.CREATED_AT || 0)) * mul
            );
          default:
            return 0;
        }
      });
    }
    return list;
  }, [
    rows,
    statusFilter,
    qDeb,
    showOverdueOnly,
    showFocusOnly,
    includeFinished,
    sort,
    getDevName,
  ]);

  /* ==================== UPDATE helpers ==================== */
  const updateProjeto = async (id, payload) => {
    if (ProjetosApi?.update) {
      try {
        return await ProjetosApi.update(id, payload);
      } catch {}
    }
    try {
      return await api.patch(`/projetos/${id}`, payload);
    } catch {
      return await api.put(`/projetos/${id}`, payload);
    }
  };

  const updateStatus = async (id, payload) => {
    const body = {};
    if (payload.status) {
      const st = normalizeStatus(payload.status);
      if (st) body.status = st;
    }
    if (typeof payload.progresso === "number")
      body.progresso = Math.max(
        0,
        Math.min(100, Math.round(payload.progresso))
      );
    if (payload.descricao !== undefined)
      body.descricao = String(payload.descricao || "");

    // ✅ Agora SEM fallback pro /projetos
    return await api.patch(`/projetos/${id}/status`, body);
  };

  // A sessão ativa é a fonte oficial do trabalho atual.
  // O status global do projeto pode estar EM_ANDAMENTO por outro colaborador.
  const getInProgress = () => {
    if (!atividadeAtual?.ID_SESSAO) return null;

    return (
      rows.find(
        (item) =>
          Number(item.ID_PROJETO) === Number(atividadeAtual.ID_PROJETO)
      ) || {
        ID_PROJETO: atividadeAtual.ID_PROJETO,
        TITULO: atividadeAtual.PROJETO_TITULO,
        STATUS: atividadeAtual.PROJETO_STATUS,
      }
    );
  };

  // const ensureNoOtherInProgress = (nextId) => {
  //   const current = getInProgress();
  //   if (current && Number(current.ID_PROJETO) !== Number(nextId)) {
  //     showError(
  //       `Você já está trabalhando no projeto #${current.ID_PROJETO} — ${
  //         current.TITULO || atividadeAtual?.PROJETO_TITULO || "-"
  //       }. Use "Trocar atividade" para preservar o tempo e a interrupção.`
  //     );
  //     return false;
  //   }
  //   return true;
  // };

  const quickStatus = async (id, status, progresso) => {
    if (!id) return;
    const rowAtual = rows.find((item) => item.ID_PROJETO === id);
    const canon = normalizeStatus(status);
    const old = normalizeStatus(rowAtual?.STATUS);

    if (canon === STATUS.EM_ANDAMENTO) {
      const tarefaSelecionada =
        Number(manageId) === Number(id) ? Number(mf?.idTarefa) || null : null;
      await iniciarAtividadeProjeto(id, tarefaSelecionada);
      return;
    }

    if (
      canon === STATUS.PAUSADO &&
      Number(atividadeAtual?.ID_PROJETO) === Number(id)
    ) {
      openOperational("PAUSAR", {
        motivo: "",
      });
      return;
    }

    if (
      isProjetoOriginadoDeChamado(rowAtual) &&
      canon &&
      STATUS_BLOQUEADOS_PROJETO_CHAMADO.has(canon) &&
      canon !== old
    ) {
      showError(
        "Projeto originado de chamado deve ser testado e aprovado e depois encaminhado ao suporte.",
      );
      return;
    }

    const proceed = async (comentarioOpt) => {
      const snapshot = rows;
      // otimista
      setRows((prev) =>
        prev.map((r) =>
          r.ID_PROJETO === id
            ? {
                ...r,
                STATUS: canon || r.STATUS,
                PROGRESSO:
                  typeof progresso === "number"
                    ? Math.max(0, Math.min(100, Math.round(progresso)))
                    : r.PROGRESSO,
              }
            : r
        )
      );
      try {
        const body = {};
        if (canon) body.status = canon;
        if (typeof progresso === "number") body.progresso = progresso;

        if (
          atividadeAtual?.ID_SESSAO &&
          Number(atividadeAtual.ID_PROJETO) === Number(id) &&
          canon &&
          canon !== STATUS.EM_ANDAMENTO
        ) {
          await ProjetosApi.pararTrabalho({
            devId: Number(devId),
            idSessao: atividadeAtual.ID_SESSAO,
            motivoEncerramento: `STATUS_${canon}`,
            observacao: comentarioOpt || `Alteração para ${canon}`,
            statusSessao: "ENCERRADA",
            pausarAtividade: canon === STATUS.PAUSADO,
            autorId: Number(devId),
          });
          setAtividadeAtual(null);
        }

        await updateStatus(id, body);
        if (comentarioOpt) await postCommentQuick(id, comentarioOpt, devId);

        showInfo("Atualizado.");
      } catch (e) {
        if (e?.response?.status === 409) {
          showError(
            e.response.data?.message || "Conflito: já existe item EM ANDAMENTO."
          );
          return;
        }
        setRows(snapshot);
        showError(
          e?.response?.data?.message || "Falha ao atualizar status/progresso."
        );
      }
    };

    if (canon && canon !== old) {
      openCommentModal(
        "Motivo da mudança de status",
        `#${id}: ${STATUS_LABEL[old] || old} → ${STATUS_LABEL[canon] || canon}`,
        async (txt) => {
          closeCommentModal();
          await proceed(txt);
        }
      );
      return;
    }

    await proceed(null);
  };

  const setFocusFor = async (projId, active) => {
    const curr = rows.find((r) => r.FOCUS_ATIVO) || null;
    const myId = Number(devId) || null;
    if (!myId) return;

    if (active && curr && curr.ID_PROJETO !== projId) {
      try {
        await (ProjetosApi.definirFoco
          ? ProjetosApi.definirFoco(curr.ID_PROJETO, { devId: myId, ativo: false })
          : api.post(`/projetos/${curr.ID_PROJETO}/foco`, {
              devId: myId,
              ativo: false,
            }));
      } catch {}
    }
    try {
      await (ProjetosApi.definirFoco
        ? ProjetosApi.definirFoco(projId, {
            devId: myId,
            ativo: !!active,
            motivo: "DEV",
          })
        : api.post(`/projetos/${projId}/foco`, {
            devId: myId,
            ativo: !!active,
            motivo: "DEV",
          }));
      setRows((prev) =>
        prev.map((r) => ({
          ...r,
          FOCUS_ATIVO:
            r.ID_PROJETO === projId ? !!active : active ? false : r.FOCUS_ATIVO,
        }))
      );
      showInfo(active ? "Foco ativado." : "Foco removido.");
    } catch (e) {
      showError(e?.response?.data?.message || "Falha ao alterar foco.");
    }
  };

  /* -------- Inline Progress -------- */
  const [editingProgId, setEditingProgId] = useState(null);
  const [editingProgVal, setEditingProgVal] = useState(0);
  const [savingProg, setSavingProg] = useState(false);

  const startEditProg = (r) => {
    setEditingProgId(r.ID_PROJETO);
    setEditingProgVal(Number(r.PROGRESSO || 0));
  };
  const cancelEditProg = () => {
    setEditingProgId(null);
    setSavingProg(false);
  };
  const saveProg = async (id, val) => {
    const v = Math.max(0, Math.min(100, Math.round(Number(val) || 0)));
    setSavingProg(true);
    const snapshot = rows;
    setRows((prev) =>
      prev.map((r) => (r.ID_PROJETO === id ? { ...r, PROGRESSO: v } : r))
    );
    try {
      await updateStatus(id, { progresso: v });
      showInfo("Progresso atualizado.");
      setManageDetail((prev) => (prev ? { ...prev, PROGRESSO: v } : prev));
    } catch (e) {
      setRows(snapshot);
      showError(e?.response?.data?.message || "Falha ao atualizar progresso.");
    } finally {
      setSavingProg(false);
      setEditingProgId(null);
    }
  };

  /* -------- Detalhe -------- */
  const [openAccordions, setOpenAccordions] = useState({
    detalhes: true,
    controle: false,
    equipe: false,
    comentarios: false,
    anexos: false,
    historico: false,
  });

  const toggleAccordion = (key) => {
    setOpenAccordions({
      detalhes: key === "detalhes",
      controle: key === "controle",
      equipe: key === "equipe",
      comentarios: key === "comentarios",
      anexos: key === "anexos",
      historico: key === "historico",
    });
  };
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [postingComment, setPostingComment] = useState(false);

  // === Anexos
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const normalizeAttachmentsList = (list) =>
    (list || []).map((a) => {
      const id = a.ID_ANEXO ?? a.id ?? a.ID;
      const name =
        a.NOME_ARQUIVO ?? a.NOME ?? a.nome ?? a.filename ?? `arquivo_${id}`;
      const url = a.DISPLAY_URL ?? a.URL ?? a.url ?? a.display_url;
      const downloadUrl = a.DOWNLOAD_URL ?? a.download_url ?? null;

      return {
        id,
        name,
        url,
        downloadUrl,
        dl: buildDownloadUrl(downloadUrl || url, name),
        sizeHuman: a.TAMANHO_HUMANO ?? a.sizeHuman ?? null,
        contentType: a.CONTENT_TYPE ?? a.contentType ?? "",
      };
    });

  const fetchAttachments = async (projectId) => {
    setLoadingAttachments(true);
    try {
      const { data } = await api.get(`/projetos/${projectId}/anexos`);
      const list = Array.isArray(data) ? data : data?.data || [];
      setAttachments(normalizeAttachmentsList(list));
    } catch (e) {
      showError(e?.response?.data?.message || "Falha ao carregar anexos.");
    } finally {
      setLoadingAttachments(false);
    }
  };

  const uploadAttachment = async () => {
    if (!manageDetail?.ID_PROJETO || !fileToUpload) return;

    const tmpUrl = URL.createObjectURL(fileToUpload);
    const tmpId = `tmp-${Date.now()}`;
    const tmp = {
      id: tmpId,
      name: fileToUpload.name || "arquivo",
      url: tmpUrl,
      downloadUrl: null,
      sizeHuman: `${Math.max(1, Math.round(fileToUpload.size / 1024))} KB`,
      contentType: fileToUpload.type || "",
      __optimistic: true,
    };
    setAttachments((prev) => [tmp, ...prev]);
    setUploadingAttachment(true);

    try {
      const form = new FormData();
      form.append("file", fileToUpload);
      await api.post(`/projetos/${manageDetail.ID_PROJETO}/anexos`, form);
      await fetchAttachments(manageDetail.ID_PROJETO);
      showInfo("Anexo enviado.");
    } catch (e) {
      setAttachments((prev) => prev.filter((a) => a.id !== tmpId));
      showError(e?.response?.data?.message || "Falha ao enviar anexo.");
    } finally {
      setUploadingAttachment(false);
      setFileToUpload(null);
      try {
        URL.revokeObjectURL(tmpUrl);
      } catch {}
    }
  };

  const deleteAttachment = async (anexo) => {
    if (!manageDetail?.ID_PROJETO || !anexo?.id) return;
    if (!window.confirm("Remover este anexo?")) return;
    const snapshot = attachments;
    setAttachments((prev) => prev.filter((a) => a.id !== anexo.id));
    try {
      await api.delete(
        `/projetos/${manageDetail.ID_PROJETO}/anexos/${anexo.id}`
      );
      showInfo("Anexo removido.");
    } catch (e) {
      setAttachments(snapshot);
      showError(e?.response?.data?.message || "Falha ao remover anexo.");
    }
  };

  /* -------- Gestão -------- */
  const [manageOpen, setManageOpen] = useState(false);
  const [manageId, setManageId] = useState(null);
  const [savingManage, setSavingManage] = useState(false);
  const [mf, setMf] = useState({
    status: "",
    progresso: 0,
    dtInicio: "",
    dtPrevista: "",
    foco: false,
    idTarefa: "",
  });

  const openManage = async (id) => {
    setManageId(id);
    setManageOpen(true);
    setOpenAccordions({
      detalhes: true,
      controle: false,
      equipe: false,
      comentarios: false,
      anexos: false,
      historico: false,
    });
    setComments([]);
    setAttachments([]);
    setProjectTasks([]);
    setProjectParticipants([]);
    setLoadingComments(false);
    setLoadingAttachments(false);
    try {
      const { data } = ProjetosApi?.get
        ? await ProjetosApi.get(id)
        : await api.get(`/projetos/${id}`);
      const normStatus = normalizeStatus(data?.STATUS) || "";
      const { id: creatorId, nome: creatorName } = pickCreator(data || {});
      const rowAtual = rows.find((r) => r.ID_PROJETO === id);
      if (creatorId && creatorName) mergeNames({ [creatorId]: String(creatorName) });
      const tarefasProjeto = Array.isArray(data?.tarefas)
        ? data.tarefas
        : [];
      const participantesProjeto = Array.isArray(data?.atribuidos)
        ? data.atribuidos
        : [];

      setProjectTasks(tarefasProjeto);
      setProjectParticipants(participantesProjeto);
      ensureDevNames(
        participantesProjeto.map((item) => item?.ID_DEV).filter(Boolean)
      ).catch(() => {});

      setManageDetail({
        ...data,
        STATUS: normStatus,
        CRIADOR_ID: creatorId || data?.CRIADOR_ID || rowAtual?.CRIADOR_ID || null,
        CREATED_AT: data?.CREATED_AT || data?.createdAt || rowAtual?.CREATED_AT || null,
      });

      const tarefaAtivaDoDev = tarefasProjeto.find(
        (item) =>
          Number(item.RESPONSAVEL_ID) === Number(devId) &&
          normalizeStatus(item.STATUS) !== STATUS.CONCLUIDO
      );

      setMf({
        status: normalizeStatus(data?.STATUS) || "",
        progresso: Number(data?.PROGRESSO || 0),
        dtInicio: isoToLocalInput(data?.DT_INICIO),
        dtPrevista: isoToLocalInput(data?.DT_PREVISTA),
        foco: !!rows.find((r) => r.ID_PROJETO === id)?.FOCUS_ATIVO,
        idTarefa: tarefaAtivaDoDev?.ID_TAREFA
          ? String(tarefaAtivaDoDev.ID_TAREFA)
          : "",
      });
    } catch {
      showError("Falha ao carregar dados do projeto.");
      setManageOpen(false);
    }
  };

  const saveManage = async () => {
    const id = manageId;
    const row = rows.find((r) => r.ID_PROJETO === id);
    if (!row) return;

    const oldStatus = normalizeStatus(manageDetail?.STATUS ?? row.STATUS);
    const newStatus = normalizeStatus(mf.status);

    if (
      isProjetoOriginadoDeChamado(manageDetail ?? row) &&
      newStatus &&
      STATUS_BLOQUEADOS_PROJETO_CHAMADO.has(newStatus) &&
      newStatus !== oldStatus
    ) {
      showError(
        "Projetos originados de chamados não podem ser finalizados ou concluídos diretamente.",
      );
      return;
    }

    const statusBody = {};
    if (newStatus) statusBody.status = newStatus;
    if (Number.isFinite(Number(mf.progresso))) {
      statusBody.progresso = Math.max(
        0,
        Math.min(100, Math.round(Number(mf.progresso)))
      );
    }

    const projPatch = {
      ...(mf.dtInicio ? { dtInicio: toIsoWithSeconds(mf.dtInicio) } : {}),
      ...(mf.dtPrevista ? { dtPrevista: toIsoWithSeconds(mf.dtPrevista) } : {}),
    };

    // nada a fazer
    if (
      !newStatus &&
      !("progresso" in statusBody) &&
      Object.keys(projPatch).length === 0
    ) {
      showInfo("Nada a atualizar.");
      return;
    }
    if (
      newStatus === STATUS.EM_ANDAMENTO &&
      oldStatus !== STATUS.EM_ANDAMENTO
    ) {
      showError(
        'Use o botão "Iniciar trabalho". O status EM ANDAMENTO agora depende de uma sessão de trabalho.'
      );
      return;
    }
    const doPersist = async (comentarioOpt) => {
      setSavingManage(true);
      const snapshot = rows;

      // otimista
      setRows((prev) =>
        prev.map((r) =>
          r.ID_PROJETO === id
            ? {
                ...r,
                STATUS: newStatus || r.STATUS,
                PROGRESSO:
                  "progresso" in statusBody
                    ? statusBody.progresso
                    : r.PROGRESSO,
                DT_PREVISTA: projPatch.dtPrevista
                  ? new Date(projPatch.dtPrevista).toISOString()
                  : r.DT_PREVISTA,
              }
            : r
        )
      );

      try {
        const calls = [];
        if (Object.keys(statusBody).length)
          calls.push(updateStatus(id, statusBody));
        if (Object.keys(projPatch).length)
          calls.push(updateProjeto(id, projPatch));
        if (calls.length) await Promise.all(calls);

        if (comentarioOpt) await postCommentQuick(id, comentarioOpt, devId);

        // sincroniza detalhe
        setManageDetail((prev) =>
          prev
            ? {
                ...prev,
                STATUS: newStatus || prev.STATUS,
                PROGRESSO:
                  "progresso" in statusBody
                    ? statusBody.progresso
                    : prev.PROGRESSO,
                DT_PREVISTA: projPatch.dtPrevista
                  ? new Date(projPatch.dtPrevista).toISOString()
                  : prev.DT_PREVISTA,
              }
            : prev
        );

        showInfo("Projeto atualizado.");
        setManageOpen(false);
      } catch (e) {
        setRows(snapshot);
        showError(e?.response?.data?.message || "Falha ao atualizar projeto.");
      } finally {
        setSavingManage(false);
      }
    };

    // exige comentário apenas se mudou status
    if (newStatus && newStatus !== oldStatus) {
      openCommentModal(
        "Motivo da mudança de status",
        `#${id}: ${STATUS_LABEL[oldStatus] || oldStatus} → ${
          STATUS_LABEL[newStatus] || newStatus
        }`,
        async (txt) => {
          closeCommentModal();
          await doPersist(txt);
        }
      );
      return; // aguarda ação do modal
    }

    // sem mudança de status, segue direto
    await doPersist(null);
  };

  /* -------- Comentários -------- */
  const fetchComments = async (projectId) => {
    setLoadingComments(true);
    try {
      const { data } = await api.get(`/projetos/${projectId}/comentarios`);
      const list = Array.isArray(data) ? data : data?.data || [];
      setComments(list);
      const ids = list.map((c) => Number(c?.AUTOR_ID)).filter(Boolean);
      await ensureDevNames(ids);
    } catch (e) {
      showError(e?.response?.data?.message || "Falha ao carregar comentários.");
    } finally {
      setLoadingComments(false);
    }
  };

  const postComment = async () => {
    if (!manageDetail?.ID_PROJETO) return;
    const texto = (commentText || "").trim();
    if (!texto) return;
    const autorIdNum = Number(devId) || null;

    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      ID_COMENTARIO: tempId,
      ID_PROJETO: manageDetail.ID_PROJETO,
      AUTOR_ID: autorIdNum,
      TEXTO: texto,
      CREATED_AT: new Date().toISOString(),
      _optimistic: true,
    };

    setPostingComment(true);
    setComments((prev) => [optimistic, ...prev]);
    setCommentText("");

    try {
      const { data } = await api.post(
        `/projetos/${manageDetail.ID_PROJETO}/comentarios`,
        { texto, autorId: autorIdNum || undefined }
      );
      if (data?.AUTOR_ID && pickName(data))
        mergeNames({ [Number(data.AUTOR_ID)]: String(pickName(data)) });
      setComments((prev) =>
        prev.map((c) => (c.ID_COMENTARIO === tempId ? data || c : c))
      );
      await ensureDevNames([autorIdNum]);
      showInfo("Comentário incluído.");
    } catch (e) {
      setComments((prev) => prev.filter((c) => c.ID_COMENTARIO !== tempId));
      setCommentText(texto);
      showError(e?.response?.data?.message || "Falha ao incluir comentário.");
    } finally {
      setPostingComment(false);
    }
  };

  const deleteComment = async (c) => {
    if (!manageDetail?.ID_PROJETO || !c?.ID_COMENTARIO) return;
    if (!window.confirm("Remover este comentário?")) return;
    const snapshot = [...comments];
    setComments((prev) =>
      prev.filter((x) => x.ID_COMENTARIO !== c.ID_COMENTARIO)
    );
    try {
      await api.delete(
        `/projetos/${manageDetail.ID_PROJETO}/comentarios/${c.ID_COMENTARIO}`
      );
      showInfo("Comentário removido.");
    } catch (e) {
      setComments(snapshot);
      showError(e?.response?.data?.message || "Falha ao remover comentário.");
    }
  };

  // Lazy-load de comentários/anexos quando o accordion correspondente é aberto.
  useEffect(() => {
    if (!manageOpen || !manageDetail?.ID_PROJETO) return;
    if (openAccordions.comentarios) fetchComments(manageDetail.ID_PROJETO);
    if (openAccordions.anexos) fetchAttachments(manageDetail.ID_PROJETO);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAccordions.comentarios, openAccordions.anexos, manageOpen, manageDetail?.ID_PROJETO]);

  const myFocus = rows.find((r) => r.FOCUS_ATIVO) || null;
  const funcionarioLogado = useMemo(
    () => getSessionValue("RazaoSocial", "Funcionario", "funcionario"),
    [],
  );
  const codFuncLogado = useMemo(
    () => getSessionValue("codFunc", "CodFunc", "CODFUNC"),
    [],
  );
  const autorIdLogado = useMemo(
    () => Number(getSessionValue("IdFuncionario", "Idfuncionario", "DevId", "codFunc")) || 0,
    [],
  );

  const atividadeDuracaoSegundos = useMemo(() => {
    if (!atividadeAtual?.DT_INICIO) return 0;

    const inicio = new Date(atividadeAtual.DT_INICIO).getTime();
    if (!Number.isFinite(inicio)) return 0;

    return Math.max(0, Math.floor((clockNow - inicio) / 1000));
  }, [atividadeAtual?.DT_INICIO, clockNow]);

  const queueOverview = useMemo(() => {
    const base = Array.isArray(rows) ? rows : [];
    return {
      total: base.filter(
        (item) =>
          ![
            STATUS.CONCLUIDO,
            STATUS.CANCELADO,
            STATUS.AGUARDANDO_RETORNO_CLIENTE,
          ].includes(normalizeStatus(item.STATUS)),
      ).length,
      originados: base.filter(isProjetoOriginadoDeChamado).length,
      emAndamento: base.filter(
        (item) => normalizeStatus(item.STATUS) === STATUS.EM_ANDAMENTO,
      ).length,
      paraTeste: base.filter(
        (item) => normalizeStatus(item.STATUS) === STATUS.TESTE,
      ).length,
      aprovados: base.filter(
        (item) => normalizeStatus(item.STATUS) === STATUS.TESTADO_APROVADO,
      ).length,
      atrasados: overdueTotal,
    };
  }, [rows, overdueTotal]);

  /* ===== Progress cell ===== */
  const ProgressCell = ({ row }) => {
    if (editingProgId === row.ID_PROJETO) {
      return (
        <div className="progress-edit">
          <input
            type="range"
            min={0}
            max={100}
            value={editingProgVal}
            onChange={(e) => setEditingProgVal(Number(e.target.value))}
          />
          <div className="progress-actions">
            <span className="mono">{editingProgVal}%</span>
            <Button
              variant="secondary"
              onClick={() => setEditingProgVal((v) => Math.max(0, v - 10))}
            >
              −10
            </Button>
            <Button
              variant="secondary"
              onClick={() => setEditingProgVal((v) => Math.min(100, v + 10))}
            >
              +10
            </Button>
            <Button variant="secondary" onClick={() => setEditingProgVal(100)}>
              100%
            </Button>
            <Button
              onClick={() => saveProg(row.ID_PROJETO, editingProgVal)}
              disabled={savingProg}
            >
              {savingProg ? "Salvando…" : "Salvar"}
            </Button>
            <Button
              variant="secondary"
              onClick={cancelEditProg}
              disabled={savingProg}
            >
              Cancelar
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="progress-wrap" onMouseDown={() => startEditProg(row)}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              height: 6,
              borderRadius: 999,
              background: "#e5e7eb",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                width: `${Math.max(
                  0,
                  Math.min(100, Number(row.PROGRESSO) || 0)
                )}%`,
                borderRadius: 999,
                background: "var(--mf-brand, #3b82f6)",
              }}
            />
          </div>
        </div>
        <span className="progress-label">{Number(row.PROGRESSO) || 0}%</span>
      </div>
    );
  };

  // handler de clique no cabeçalho
  const clickSort = (key) => {
    setSort((curr) => {
      const [k, d] = String(curr).split(":");
      if (k === key) {
        return `${k}:${d === "asc" ? "desc" : "asc"}`;
      }
      return `${key}:${DEFAULT_DIRS[key] || "asc"}`;
    });
  };

  /* ==================== JSX ==================== */
  const [sortKey, sortDir] = String(sort).split(":");

  const activeProjectId = Number(atividadeAtual?.ID_PROJETO) || null;
  const hasActiveWork = !!atividadeAtual?.ID_SESSAO;
  const isManagedProjectRunning =
    hasActiveWork && Number(activeProjectId) === Number(manageId);
  const shouldSwitchToManaged =
    hasActiveWork && Number(activeProjectId) !== Number(manageId);
  return (
    <MainLayout>
      <div className={`proj-page queue-page ${density === "compacto" ? "dense" : ""}`}>
        <div className="proj-toolbar queue-page-header">
          <div className="left">
            <span className="queue-eyebrow">Workspace do desenvolvimento</span>
            <h1>Minha Fila</h1>
            {myFocus && (
              <div className="hint">
                <b>Meu foco:</b> #{myFocus.ID_PROJETO} — {myFocus.TITULO}{" "}
                {myFocus.DT_PREVISTA && (
                  <em>• Prevista {fmtDateOnly(myFocus.DT_PREVISTA)}</em>
                )}
              </div>
            )}
            <div className="hint">
              Mostrando <b>{view.length}</b> de <b>{rows.length}</b>{" "}
              {includeFinished
                ? "(inclui concluídos, cancelados e aguardando cliente)"
                : "(somente fila operacional)"}{" "}
              • <b>{overdueTotal}</b> atrasados.
            </div>
          </div>
          <div className="right">
            <Button onClick={() => load(devId)} disabled={!devId || loading}>
              Atualizar
            </Button>
            <Button variant="outline" onClick={resetLayout}>
              Resetar
            </Button>
          </div>
        </div>

        <section
          className={`work-now-card ${
            atividadeAtual?.ID_SESSAO ? "is-active" : "is-idle"
          }`}
          aria-label="Atividade atual"
        >
          <div className="work-now-main">
            <span className="work-now-eyebrow">
              <span className="work-now-status-dot" aria-hidden="true" />
              {atividadeAtual?.ID_SESSAO
                ? "Trabalhando agora"
                : "Nenhuma atividade iniciada"}
            </span>

            {atividadeAtual?.ID_SESSAO ? (
              <>
                <h2>
                  #{atividadeAtual.ID_PROJETO} —{" "}
                  {atividadeAtual.PROJETO_TITULO || "Projeto"}
                </h2>
                <p>
                  {atividadeAtual.TAREFA_TITULO
                    ? `Tarefa: ${atividadeAtual.TAREFA_TITULO}`
                    : "Trabalho geral do projeto"}
                  {" • "}
                  Iniciado em {fmtDate(atividadeAtual.DT_INICIO)}
                </p>
              </>
            ) : (
              <>
                <h2>Selecione um projeto da fila para iniciar</h2>
                <p>
                  O tempo só será contabilizado após o início da sessão de
                  trabalho.
                </p>
              </>
            )}
          </div>

          <div className="work-now-actions">
            {atividadeAtual?.ID_SESSAO && (
              <strong className="work-now-timer mono">
                {formatDuration(atividadeDuracaoSegundos)}
              </strong>
            )}

            {atividadeAtual?.ID_SESSAO && (
              <Button
                variant="secondary"
                onClick={() => openOperational("PAUSAR")}
                disabled={operationalSaving}
              >
                Pausar
              </Button>
            )}

            {atividadeAtual?.ID_SESSAO && (
              <Button
                variant="secondary"
                onClick={() => openOperational("TROCAR")}
                disabled={operationalSaving}
              >
                Trocar atividade
              </Button>
            )}

            <Button
              variant="outline"
              onClick={() => openOperational("INCIDENTE")}
              disabled={operationalSaving}
            >
              Urgência não cadastrada
            </Button>
          </div>
        </section>

        {atividadeAtual?.ID_SESSAO && (
          <aside className="work-session-dock" aria-label="Controles da atividade atual">
            <div className="work-session-dock-main">
              <span className="work-session-dock-status">
                Sessão em andamento
              </span>
              <strong>
                #{atividadeAtual.ID_PROJETO} —{" "}
                {atividadeAtual.PROJETO_TITULO || "Projeto atual"}
              </strong>
              <small>
                {atividadeAtual.TAREFA_TITULO
                  ? `Tarefa: ${atividadeAtual.TAREFA_TITULO}`
                  : "Atividade geral do projeto"}
              </small>
            </div>

            <span className="work-session-dock-timer mono">
              {formatDuration(atividadeDuracaoSegundos)}
            </span>

            <div className="work-session-dock-actions">
              <Button
                variant="secondary"
                onClick={() => openManage(atividadeAtual.ID_PROJETO)}
              >
                Abrir atividade
              </Button>
              <Button
                className="work-session-dock-switch"
                onClick={() => openOperational("TROCAR")}
                disabled={operationalSaving}
              >
                Trocar atividade
              </Button>
              <Button
                variant="secondary"
                onClick={() => openOperational("PAUSAR")}
                disabled={operationalSaving}
              >
                Pausar
              </Button>
            </div>
          </aside>
        )}

        <section className="queue-overview-grid" aria-label="Resumo da fila">
          <article className="queue-overview-card total">
            <span>Fila operacional</span>
            <strong>{queueOverview.total}</strong>
            <small>Projetos ativos atribuídos</small>
          </article>
          <article className="queue-overview-card focus">
            <span>Em andamento</span>
            <strong>{queueOverview.emAndamento}</strong>
            <small>{myFocus ? `Foco #${myFocus.ID_PROJETO}` : "Nenhum foco definido"}</small>
          </article>
          <article className="queue-overview-card origin">
            <span>Vindos de chamados</span>
            <strong>{queueOverview.originados}</strong>
            <small>Exigem retorno ao cliente</small>
          </article>
          <article className="queue-overview-card test">
            <span>Para teste</span>
            <strong>{queueOverview.paraTeste}</strong>
            <small>Aguardando validação</small>
          </article>
          <article className="queue-overview-card approved">
            <span>Testados e aprovados</span>
            <strong>{queueOverview.aprovados}</strong>
            <small>Prontos para o suporte</small>
          </article>
          <article className="queue-overview-card overdue">
            <span>Atrasados</span>
            <strong>{queueOverview.atrasados}</strong>
            <small>Prazo previsto vencido</small>
          </article>
        </section>

        {/* Chips rápidos por status */}
        <div className="status-chips queue-status-chips">
          <button
            className={`chip ${!statusFilter ? "active" : ""}`}
            onClick={() => setStatusFilter("")}
            title="Todos os status"
          >
            Todos <span className="chip-count">{view.length}</span>
          </button>
          {Object.values(STATUS).map((s) => (
            <button
              key={s}
              className={`chip ${statusFilter === s ? "active" : ""}`}
              onClick={() => setStatusFilter((curr) => (curr === s ? "" : s))}
              title={STATUS_LABEL[s]}
            >
              {STATUS_LABEL[s]}{" "}
              <span className="chip-count">{countsByStatus[s] || 0}</span>
            </button>
          ))}
        </div>

        {/* Sub-toolbar */}
        <div className="filters-bar queue-filters-panel">
          <div className="queue-filters-head">
            <div>
              <strong>Filtros e visualização</strong>
              <span>Refine a fila, escolha a densidade e personalize as colunas.</span>
            </div>
            <span className="queue-results-pill">
              {view.length} de {rows.length} projeto(s)
            </span>
          </div>

          <div className="filters-scroll">
            <select
              className="input native"
              value={density}
              onChange={(e) => setDensity(e.target.value)}
              title="Densidade"
            >
              <option value="conforto">Densidade: Conforto</option>
              <option value="compacto">Densidade: Compacto</option>
            </select>

            <label className="chk">
              <input
                type="checkbox"
                checked={showOverdueOnly}
                onChange={(e) => setShowOverdueOnly(e.target.checked)}
              />
              <span>Apenas atrasados ({overdueTotal})</span>
            </label>

            <label className="chk">
              <input
                type="checkbox"
                checked={includeFinished}
                onChange={(e) => setIncludeFinished(e.target.checked)}
              />
              <span>Incluir encerrados/aguardando cliente</span>
            </label>

            <select
              className="input native"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              title="Filtrar Status"
            >
              <option value="">Status: (todos)</option>
              {Object.values(STATUS).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>

            <div
              className="inline-field"
              title="Aceita aliases como 'PARA TESTE', 'FINALIZADO', 'OK TESTE'…"
            >
              <Input
                list="status-aliases"
                placeholder="Status (aceita alias)…"
                value={statusAliasText}
                onChange={(e) => setStatusAliasText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const canon = normalizeStatus(statusAliasText);
                    if (canon) {
                      setStatusFilter(canon);
                      e.currentTarget.blur();
                    } else showError("Alias de status não reconhecido.");
                  }
                }}
                style={{ minWidth: 180 }}
              />
              <datalist id="status-aliases">
                {Object.keys(STATUS_ALIASES).map((a) => (
                  <option key={a} value={a} />
                ))}
              </datalist>
            </div>

            {/* seletor continua funcionando e espelha o estado */}
            <select
              className="input native"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              title="Ordenação"
            >
              <option value="RANK:asc">Ordenação: Rank (servidor)</option>
              <option value="DT_PREVISTA:asc">Prevista ↑</option>
              <option value="DT_PREVISTA:desc">Prevista ↓</option>
              <option value="PRIORIDADE:desc">Prioridade (crítica→)</option>
              <option value="PRIORIDADE:asc">Prioridade (←baixa)</option>
              <option value="PROGRESSO:desc">Progresso ↓</option>
              <option value="PROGRESSO:asc">Progresso ↑</option>
              <option value="SCORE:desc">Score ↓</option>
              <option value="SCORE:asc">Score ↑</option>
              <option value="ID_PROJETO:asc">ID ↑</option>
              <option value="ID_PROJETO:desc">ID ↓</option>
              <option value="CODIGO:asc">Código A→Z</option>
              <option value="CODIGO:desc">Código Z→A</option>
              <option value="TITULO:asc">Título A→Z</option>
              <option value="TITULO:desc">Título Z→A</option>
              <option value="CRIADOR:asc">Criador A→Z</option>
              <option value="CRIADOR:desc">Criador Z→A</option>
              <option value="FOCUS_ATIVO:desc">Foco primeiro</option>
              <option value="FOCUS_ATIVO:asc">Sem foco primeiro</option>
              <option value="STATUS:asc">Status A→Z</option>
              <option value="STATUS:desc">Status Z→A</option>
            </select>

            <Input
              placeholder="Buscar por ID, título ou criador…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ minWidth: 260 }}
            />

            <select
              className="input native"
              value={activeView}
              onChange={(e) => loadView(e.target.value)}
            >
              <option value="">Visões salvas…</option>
              {views.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
            <Button variant="secondary" onClick={saveView}>
              Salvar visão
            </Button>
            <Button
              variant="secondary"
              onClick={deleteView}
              disabled={!activeView}
            >
              Remover visão
            </Button>

            <div className="cols-menu" style={{ marginLeft: "auto" }}>
              <details>
                <summary className="btn btn-secondary">Colunas</summary>
                <div>
                  {[
                    ["pos", "Pos"],
                    ["id", "ID"],
                    ["codigo", "Código"],
                    ["titulo", "Título"],
                    ["criadoEm", "Criado em"],
                    ["criador", "Criado por"],

                    ["foco", "Foco"],
                    ["score", "Score"],
                    ["status", "Status"],
                    ["prioridade", "Prioridade"],
                    ["progresso", "Progresso"],
                    ["prevista", "Prevista"],
                    ["acoes", "Ações"],
                  ].map(([k, label]) => (
                    <label key={k} className="chk">
                      <input
                        type="checkbox"
                        checked={!!cols[k]}
                        onChange={(e) =>
                          setCols((p) => ({ ...p, [k]: e.target.checked }))
                        }
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </details>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="card queue-table-card">
          <div className="queue-table-header">
            <div>
              <span className="queue-section-eyebrow">Fila priorizada</span>
              <h2>Projetos atribuídos</h2>
              <p>
                Acompanhe prioridade, prazo, andamento e abra o workspace para
                executar as próximas ações.
              </p>
            </div>

            <div className="queue-table-meta">
              <strong>{view.length}</strong>
              <span>exibidos</span>
            </div>
          </div>

          <div className="table-wrap">
            {getInProgress() && (
              <div className="hint warn">
                Sessão ativa: #{getInProgress().ID_PROJETO} —{" "}
                {getInProgress().TITULO}. Para assumir outro item, use
                <b> Trocar atividade</b>.
              </div>
            )}

            <table
              className={`table-sm w-full ${
                density === "compacto" ? "table-compact" : ""
              }`}
            >
              <thead className="sticky" style={{ width: 12 }}>
                <tr>
                  {cols.pos && (
                    <SortableTh
                      active={sortKey === "RANK"}
                      dir={sortDir}
                      onClick={() => setSort("RANK:asc")} // correto: rank é do servidor
                      style={{ width: 64 }}
                    >
                      Pos
                    </SortableTh>
                  )}

                  {cols.id && (
                    <SortableTh
                      active={sortKey === "ID_PROJETO"}
                      dir={sortDir}
                      onClick={() => clickSort("ID_PROJETO")}
                      style={{ width: 68 }}
                    >
                      ID
                    </SortableTh>
                  )}
                  {cols.codigo && (
                    <SortableTh
                      active={sortKey === "CODIGO"}
                      dir={sortDir}
                      onClick={() => clickSort("CODIGO")}
                      style={{ minWidth: 120 }}
                    >
                      Código
                    </SortableTh>
                  )}
                  {cols.titulo && (
                    <SortableTh
                      active={sortKey === "TITULO"}
                      dir={sortDir}
                      onClick={() => clickSort("TITULO")}
                      style={{ minWidth: 280 }}
                    >
                      Título
                    </SortableTh>
                  )}
                  {cols.criadoEm && (
                    <SortableTh
                      active={sortKey === "CREATED_AT"}
                      dir={sortDir}
                      onClick={() => clickSort("CREATED_AT")}
                    >
                      Criado em
                    </SortableTh>
                  )}
                  {cols.criador && (
                    <SortableTh
                      active={sortKey === "CRIADOR"}
                      dir={sortDir}
                      onClick={() => clickSort("CRIADOR")}
                      style={{ minWidth: 160 }}
                    >
                      Criado por
                    </SortableTh>
                  )}
                  {cols.foco && (
                    <SortableTh
                      active={sortKey === "FOCUS_ATIVO"}
                      dir={sortDir}
                      onClick={() => clickSort("FOCUS_ATIVO")}
                      style={{ width: 80 }}
                    >
                      Foco
                    </SortableTh>
                  )}
                  {cols.score && (
                    <SortableTh
                      active={sortKey === "SCORE"}
                      dir={sortDir}
                      onClick={() => clickSort("SCORE")}
                      style={{ width: 80 }}
                    >
                      Score
                    </SortableTh>
                  )}
                  {cols.status && (
                    <SortableTh
                      active={sortKey === "STATUS"}
                      dir={sortDir}
                      onClick={() => clickSort("STATUS")}
                    >
                      Status
                    </SortableTh>
                  )}
                  {cols.prioridade && (
                    <SortableTh
                      active={sortKey === "PRIORIDADE"}
                      dir={sortDir}
                      onClick={() => clickSort("PRIORIDADE")}
                    >
                      Prioridade
                    </SortableTh>
                  )}
                  {cols.progresso && (
                    <SortableTh
                      active={sortKey === "PROGRESSO"}
                      dir={sortDir}
                      onClick={() => clickSort("PROGRESSO")}
                      style={{ minWidth: 220 }}
                    >
                      Progresso
                    </SortableTh>
                  )}
                  {cols.prevista && (
                    <SortableTh
                      active={sortKey === "DT_PREVISTA"}
                      dir={sortDir}
                      onClick={() => clickSort("DT_PREVISTA")}
                    >
                      Prevista
                    </SortableTh>
                  )}
                  {cols.acoes && (
                    <th
                      style={{
                        minWidth: 260,
                        position: "sticky",
                        right: 0,
                        background: "#fff",
                        boxShadow: "inset 1px 0 0 #eee",
                      }}
                    >
                      Ações
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      <td colSpan={12}>
                        <div className="skeleton-line" />
                      </td>
                    </tr>
                  ))
                ) : view.length === 0 ? (
                  <tr>
                    <td colSpan={12}>
                      <div className="empty-state">
                        <div className="empty-title">
                          Sem projetos no escopo
                        </div>
                        <div className="empty-sub">
                          Ajuste filtros, inclua finalizados ou limpe a visão.
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  view.map((r, idx) => {
                    const d = daysDiff(r.DT_PREVISTA);
                    const overdue =
                      d !== null &&
                      d < 0 &&
                      ![
                        STATUS.FINALIZADO,
                        STATUS.CANCELADO,
                        STATUS.CONCLUIDO,
                        STATUS.AGUARDANDO_RETORNO_CLIENTE,
                      ].includes(r.STATUS);

                    const dueSoon =
                      d !== null &&
                      d >= 0 &&
                      d <= 2 &&
                      ![
                        STATUS.FINALIZADO,
                        STATUS.CANCELADO,
                        STATUS.CONCLUIDO,
                        STATUS.AGUARDANDO_RETORNO_CLIENTE,
                      ].includes(r.STATUS);

                    const pos = sortKey === "RANK" ? r.POS ?? idx + 1 : idx + 1;
                    const rowIsRunning =
                      hasActiveWork &&
                      Number(activeProjectId) === Number(r.ID_PROJETO);
                    const rowNeedsSwitch =
                      hasActiveWork &&
                      Number(activeProjectId) !== Number(r.ID_PROJETO);

                    return (
                      <tr
                        key={r.ID_PROJETO}
                        className={[
                          overdue ? "row-overdue" : "",
                          dueSoon ? "row-warning" : "",
                          isProjetoOriginadoDeChamado(r)
                            ? "row-project-origin"
                            : "",
                          normalizeStatus(r.STATUS) === STATUS.TESTADO_APROVADO
                            ? "row-project-approved"
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {cols.pos && <td className="mono">{pos}</td>}

                        {cols.id && (
                          <td className="mono">
                            <HL text={`#${r.ID_PROJETO || "-"}`} q={qDeb} />
                          </td>
                        )}

                        {cols.codigo && (
                          <td>
                            <HL text={r.CODIGO || ""} q={qDeb} />
                          </td>
                        )}

                        {cols.titulo && (
                          <td>
                            <div className="queue-title-stack">
                              <strong>
                                <HL text={r.TITULO || ""} q={qDeb} />
                              </strong>
                              <div className="queue-title-meta">
                                {isProjetoOriginadoDeChamado(r) ? (
                                  <span className="queue-origin-chip">
                                    Chamado #{r.ID_CHAMADO_ORIGEM}
                                  </span>
                                ) : (
                                  <span className="queue-internal-chip">
                                    Projeto interno
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        )}

                        {cols.criadoEm && <td>{fmtDate(r.CREATED_AT)}</td>}

                        {cols.criador && <td>{getDevName(r.CRIADOR_ID)}</td>}

                        {cols.foco && (
                          <td>
                            <button
                              className={`btn-pill ${
                                r.FOCUS_ATIVO ? "on" : ""
                              }`}
                              onClick={() =>
                                setFocusFor(r.ID_PROJETO, !r.FOCUS_ATIVO)
                              }
                            >
                              {r.FOCUS_ATIVO ? "Em foco" : "Focar"}
                            </button>
                          </td>
                        )}

                        {cols.score && (
                          <td className="mono">
                            {Number.isFinite(r.SCORE)
                              ? r.SCORE.toFixed(1)
                              : "-"}
                          </td>
                        )}

                        {cols.status && (
                          <td>
                            <StatusBadge value={r.STATUS} />
                          </td>
                        )}

                        {cols.prioridade && (
                          <td>
                            <PriorityPill value={r.PRIORIDADE} />
                          </td>
                        )}

                        {cols.progresso && (
                          <td>
                            <ProgressCell row={r} />
                          </td>
                        )}

                        {cols.prevista && <td>{fmtDateOnly(r.DT_PREVISTA)}</td>}

                        {cols.acoes && (
                          <td className="actions-cell">
                            <div className="queue-row-action-group">
                              <Button
                                className={`queue-row-work-button ${
                                  rowIsRunning ? "is-running" : ""
                                }`}
                                size="sm"
                                onClick={() =>
                                  rowIsRunning
                                    ? openManage(r.ID_PROJETO)
                                    : iniciarAtividadeProjeto(r.ID_PROJETO)
                                }
                                disabled={operationalSaving}
                                title={
                                  rowIsRunning
                                    ? "Esta atividade já está em andamento."
                                    : rowNeedsSwitch
                                    ? "Interrompe a atividade atual e inicia este projeto."
                                    : "Inicia o cronômetro deste projeto."
                                }
                              >
                                {rowIsRunning
                                  ? "Em andamento"
                                  : rowNeedsSwitch
                                  ? "Trocar para este"
                                  : "Iniciar trabalho"}
                              </Button>

                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => openManage(r.ID_PROJETO)}
                                title="Abrir detalhes e gestão do projeto"
                              >
                                Abrir
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal com abas: Workspace do Projeto */}
        <PanelModal
          open={!!manageOpen}
          size="xl"
          title={
            manageDetail
              ? `Projeto #${manageId} — ${manageDetail.TITULO}`
              : manageId
              ? `Projeto #${manageId}`
              : "Projeto"
          }
          onClose={() => setManageOpen(false)}
        >
          {({ firstFieldRef }) => (
            <div className="project-workspace-tabs">
              {manageDetail && (
                <div className="workspace-hero">
                  <div className="workspace-hero-main">
                    <span className="mono workspace-project-id">
                      #{manageDetail.ID_PROJETO || manageId}
                    </span>
                    <h3>{manageDetail.TITULO || "Projeto sem título"}</h3>
                    <p>
                      Código: <b>{manageDetail.CODIGO || "-"}</b> • Criado por:{" "}
                      <b>{getDevName(manageDetail.CRIADOR_ID)}</b>
                    </p>
                    {isProjetoOriginadoDeChamado(manageDetail) ? (
                      <span className="workspace-origin-chip">
                        Originado do chamado #{manageDetail.ID_CHAMADO_ORIGEM}
                      </span>
                    ) : null}
                  </div>
                  <div className="workspace-hero-badges">
                    <StatusBadge value={manageDetail.STATUS} />
                    <PriorityPill value={manageDetail.PRIORIDADE || "MEDIA"} />
                    {mf.foco && <span className="pill pill-sky">EM FOCO</span>}
                  </div>
                </div>
              )}

              {manageDetail && (
                <section
                  className={`workspace-operation-bar ${
                    isManagedProjectRunning
                      ? "is-current"
                      : shouldSwitchToManaged
                      ? "has-other-active"
                      : "is-idle"
                  }`}
                  aria-label="Ações de trabalho do projeto"
                >
                  <div className="workspace-operation-copy">
                    <span>
                      {isManagedProjectRunning
                        ? "Este projeto está em execução"
                        : shouldSwitchToManaged
                        ? "Você já está trabalhando em outro projeto"
                        : "Nenhuma sessão ativa"}
                    </span>

                    <strong>
                      {isManagedProjectRunning
                        ? `Cronômetro em ${formatDuration(
                            atividadeDuracaoSegundos
                          )}`
                        : shouldSwitchToManaged
                        ? `Atual: #${atividadeAtual?.ID_PROJETO} — ${
                            atividadeAtual?.PROJETO_TITULO || "Projeto"
                          }`
                        : "Inicie o trabalho para contabilizar o tempo desta entrega."}
                    </strong>

                    <small>
                      {isManagedProjectRunning
                        ? "Você pode trocar de atividade, pausar ou registrar uma urgência sem perder o histórico."
                        : shouldSwitchToManaged
                        ? "Ao trocar, a sessão atual será encerrada como interrupção e este projeto será iniciado."
                        : "O status EM ANDAMENTO será aplicado automaticamente ao iniciar a sessão."}
                    </small>
                  </div>

                  <div className="workspace-operation-actions">
                    {isManagedProjectRunning ? (
                      <>
                        <Button
                          className="workspace-primary-operation"
                          onClick={() => openOperational("TROCAR")}
                          disabled={operationalSaving}
                        >
                          Trocar atividade
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => openOperational("PAUSAR")}
                          disabled={operationalSaving}
                        >
                          Pausar atividade
                        </Button>
                      </>
                    ) : (
                      <Button
                        className="workspace-primary-operation"
                        onClick={() =>
                          iniciarAtividadeProjeto(
                            manageId,
                            Number(mf.idTarefa) || null
                          )
                        }
                        disabled={operationalSaving}
                      >
                        {shouldSwitchToManaged
                          ? "Interromper atual e iniciar este"
                          : "Iniciar trabalho agora"}
                      </Button>
                    )}

                    <Button
                      variant="outline"
                      onClick={() => openOperational("INCIDENTE")}
                      disabled={operationalSaving}
                    >
                      Registrar urgência
                    </Button>
                  </div>
                </section>
              )}

              {manageDetail && isProjetoOriginadoDeChamado(manageDetail) ? (
                <div className="workspace-flow-card">
                  <ProjetoFluxoChamado
                    projeto={manageDetail}
                    funcionario={funcionarioLogado}
                    codFunc={codFuncLogado}
                    autorId={autorIdLogado}
                    onUpdated={async () => {
                      await openManage(manageDetail.ID_PROJETO);
                      await load(devId);
                    }}
                  />
                </div>
              ) : null}

              <nav
                className="workspace-tabs"
                role="tablist"
                aria-label="Áreas do projeto"
              >
                {[
                  ["detalhes", "Visão geral", null],
                  ["controle", "Controle", null],
                  [
                    "equipe",
                    "Equipe e tarefas",
                    projectParticipants.length || projectTasks.length
                      ? `${projectParticipants.length + projectTasks.length}`
                      : null,
                  ],
                  [
                    "comentarios",
                    "Comentários",
                    comments.length ? `${comments.length}` : null,
                  ],
                  [
                    "anexos",
                    "Anexos",
                    attachments.length ? `${attachments.length}` : null,
                  ],
                  ["historico", "Histórico", null],
                ].map(([key, label, badge]) => (
                  <button
                    key={key}
                    id={`workspace-tab-${key}`}
                    type="button"
                    role="tab"
                    aria-selected={!!openAccordions[key]}
                    aria-controls={`workspace-panel-${key}`}
                    className={`workspace-tab ${
                      openAccordions[key] ? "active" : ""
                    }`}
                    onClick={() => toggleAccordion(key)}
                  >
                    <span>{label}</span>
                    {badge && (
                      <small>{badge}</small>
                    )}
                  </button>
                ))}
              </nav>

              <AccordionSection
                id="detalhes"
                title="Detalhes da Solicitação"
                subtitle="Escopo, contexto e informações gerais do projeto"
                open={!!openAccordions.detalhes}
                onToggle={toggleAccordion}
              >
                {manageDetail ? (
                  <div className="workspace-details">
                    <div className="grid grid-3 detail-kpis">
                      <div>
                        <b>Código:</b> {manageDetail.CODIGO || "-"}
                      </div>
                      <div>
                        <b>Status:</b> <StatusBadge value={manageDetail.STATUS} />
                      </div>
                      <div>
                        <b>Prioridade:</b>{" "}
                        <PriorityPill value={manageDetail.PRIORIDADE || "MEDIA"} />
                      </div>
                      <div>
                        <b>Progresso:</b> {Number(manageDetail.PROGRESSO || 0)}%
                      </div>
                      <div>
                        <b>Prevista:</b> {fmtDateOnly(manageDetail.DT_PREVISTA)}
                      </div>
                      <div>
                        <b>Início:</b> {fmtDate(manageDetail.DT_INICIO)}
                      </div>
                      <div>
                        <b>Criado em:</b> {fmtDate(manageDetail.CREATED_AT)}
                      </div>
                      <div>
                        <b>Criado por:</b> {getDevName(manageDetail.CRIADOR_ID)}
                      </div>
                      <div>
                        <b>Foco:</b> {mf.foco ? "Sim" : "Não"}
                      </div>
                    </div>

                    <div className="detail-section">
                      <h3>Solicitação / Escopo</h3>
                      <div className="desc detail-description">
                        {manageDetail.DESCRICAO ||
                          manageDetail.DESCRICAO_PROJETO ||
                          manageDetail.ESCOPO ||
                          manageDetail.DESCRICAO_SOLICITACAO ||
                          manageDetail.OBSERVACAO ||
                          "Sem descrição informada."}
                      </div>
                    </div>

                    {(manageDetail.OBS || manageDetail.OBSERVACOES) && (
                      <div className="detail-section">
                        <h3>Observações</h3>
                        <div className="desc detail-description">
                          {manageDetail.OBS || manageDetail.OBSERVACOES}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="empty-sub">Carregando detalhes...</div>
                )}
              </AccordionSection>

              <AccordionSection
                id="controle"
                title="Controle do Projeto"
                subtitle="Status, progresso, datas, foco e atalhos operacionais"
                open={!!openAccordions.controle}
                onToggle={toggleAccordion}
              >
                <div className="grid grid-2 workspace-control-grid">
                  <div>
                    <Label>Status</Label>
                    <select
                      ref={firstFieldRef}
                      className="input native"
                      value={mf.status}
                      onChange={(e) =>
                        setMf((p) => ({ ...p, status: e.target.value }))
                      }
                    >
                      <option value="">(manter)</option>
                      {getStatusOptionsForProject(
                        manageDetail,
                        mf.status || manageDetail?.STATUS,
                      ).filter(
                        (s) =>
                          s !== STATUS.EM_ANDAMENTO ||
                          s ===
                            normalizeStatus(
                              mf.status || manageDetail?.STATUS
                            )
                      )
                      .map((s) => (
                        <option
                          key={s}
                          value={s}
                          disabled={
                            isProjetoOriginadoDeChamado(manageDetail) &&
                            STATUS_BLOQUEADOS_PROJETO_CHAMADO.has(s) &&
                            s === normalizeStatus(mf.status || manageDetail?.STATUS)
                          }
                        >
                          {STATUS_LABEL[s]}
                        </option>
                      ))}
                    </select>
                    {isProjetoOriginadoDeChamado(manageDetail) ? (
                      <small className="workspace-status-lock">
                        Fluxo protegido: use TESTADO E APROVADO e depois
                        Encaminhar ao suporte.
                      </small>
                    ) : null}
                  </div>

                  <div>
                    <Label>Tarefa para iniciar</Label>
                    <select
                      className="input native"
                      value={mf.idTarefa || ""}
                      onChange={(e) =>
                        setMf((prev) => ({
                          ...prev,
                          idTarefa: e.target.value,
                        }))
                      }
                    >
                      <option value="">Trabalho geral do projeto</option>
                      {projectTasks.map((tarefa) => (
                        <option
                          key={tarefa.ID_TAREFA}
                          value={tarefa.ID_TAREFA}
                          disabled={
                            normalizeStatus(tarefa.STATUS) ===
                            STATUS.CONCLUIDO
                          }
                        >
                          #{tarefa.ID_TAREFA} — {tarefa.TITULO}
                          {tarefa.RESPONSAVEL_ID
                            ? ` (${getDevName(tarefa.RESPONSAVEL_ID)})`
                            : ""}
                        </option>
                      ))}
                    </select>
                    <small>
                      O cronômetro será vinculado à tarefa selecionada.
                    </small>
                  </div>

                  <div>
                    <Label>Progresso</Label>
                    <div className="workspace-range-line">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        className="w-full"
                        value={Number(mf.progresso) || 0}
                        onChange={(e) =>
                          setMf((p) => ({
                            ...p,
                            progresso: Number(e.target.value),
                          }))
                        }
                      />
                      <span className="mono">{Number(mf.progresso) || 0}%</span>
                    </div>
                  </div>

                  <div>
                    <Label>Início</Label>
                    <Input
                      type="datetime-local"
                      value={mf.dtInicio}
                      onChange={(e) =>
                        setMf((p) => ({ ...p, dtInicio: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <Label>Prevista</Label>
                    <Input
                      type="datetime-local"
                      value={mf.dtPrevista}
                      onChange={(e) =>
                        setMf((p) => ({ ...p, dtPrevista: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div className="card soft workspace-shortcuts">
                  <div className="workspace-section-title">Atalhos</div>
                  <div className="workspace-shortcut-actions">
                    <Button
                      className="workspace-shortcut-primary"
                      onClick={() =>
                        isManagedProjectRunning
                          ? openOperational("TROCAR")
                          : iniciarAtividadeProjeto(
                              manageId,
                              Number(mf.idTarefa) || null
                            )
                      }
                      disabled={operationalSaving}
                      title={
                        isManagedProjectRunning
                          ? "Escolha outra atividade e registre a interrupção desta sessão."
                          : shouldSwitchToManaged
                          ? "Interrompe a atividade atual e inicia este projeto."
                          : "Inicia o cronômetro desta atividade."
                      }
                    >
                      {isManagedProjectRunning
                        ? "Trocar atividade"
                        : shouldSwitchToManaged
                        ? "Trocar para este projeto"
                        : "Iniciar trabalho"}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        isManagedProjectRunning
                          ? openOperational("PAUSAR")
                          : quickStatus(manageId, "PAUSADO")
                      }
                      disabled={operationalSaving}
                    >
                      {isManagedProjectRunning
                        ? "Pausar atividade"
                        : "Marcar projeto como pausado"}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => quickStatus(manageId, "TESTE")}
                    >
                      Enviar p/ Teste
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        quickStatus(manageId, "TESTADO_APROVADO", 100)
                      }
                    >
                      Marcar Aprovado
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => quickStatus(manageId, "TESTADO_REPROVADO")}
                    >
                      Marcar Reprovado
                    </Button>
                  </div>
                </div>
              </AccordionSection>

              <AccordionSection
                id="equipe"
                title="Equipe e tarefas"
                subtitle="Participantes do projeto e divisão operacional da entrega"
                open={!!openAccordions.equipe}
                onToggle={toggleAccordion}
                badge={`${projectParticipants.filter((item) => item.ATIVO).length} / ${projectTasks.length}`}
              >
                <div className="workspace-team-grid">
                  <div className="card soft">
                    <div className="workspace-section-title">Participantes</div>
                    {projectParticipants.length === 0 ? (
                      <div className="empty-sub">Nenhum participante informado.</div>
                    ) : (
                      <ul className="workspace-simple-list">
                        {projectParticipants.map((item) => (
                          <li key={`${item.ID_DEV}-${item.ID_ATRIBUICAO}`}>
                            <div>
                              <b>{getDevName(item.ID_DEV)}</b>
                              <small>
                                {item.PAPEL || "DESENVOLVEDOR"}
                                {item.RESPONSAVEL_PRINCIPAL
                                  ? " • Responsável principal"
                                  : ""}
                              </small>
                            </div>
                            <span
                              className={`badge ${
                                item.TRABALHANDO_AGORA
                                  ? "badge-emerald"
                                  : item.ATIVO
                                  ? "badge-blue"
                                  : "badge-gray"
                              }`}
                            >
                              {item.TRABALHANDO_AGORA
                                ? "TRABALHANDO"
                                : item.ATIVO
                                ? "ATIVO"
                                : "INATIVO"}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="card soft">
                    <div className="workspace-section-title">Tarefas</div>
                    {projectTasks.length === 0 ? (
                      <div className="empty-sub">
                        Nenhuma tarefa cadastrada. O trabalho poderá ser
                        registrado no projeto geral.
                      </div>
                    ) : (
                      <ul className="workspace-simple-list">
                        {projectTasks.map((tarefa) => (
                          <li key={tarefa.ID_TAREFA}>
                            <div>
                              <b>
                                #{tarefa.ID_TAREFA} — {tarefa.TITULO}
                              </b>
                              <small>
                                {tarefa.RESPONSAVEL_ID
                                  ? getDevName(tarefa.RESPONSAVEL_ID)
                                  : "Sem responsável"}
                                {" • "}
                                {Number(tarefa.GASTO_H || 0).toFixed(2)}h
                                realizadas
                              </small>
                            </div>
                            <StatusBadge value={tarefa.STATUS} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </AccordionSection>

              <AccordionSection
                id="comentarios"
                title="Comentários"
                subtitle="Registro de decisões, andamento e observações técnicas"
                open={!!openAccordions.comentarios}
                onToggle={toggleAccordion}
                badge={comments.length ? `${comments.length}` : null}
              >
                <div>
                  <div className="grid grid-4">
                    <div className="col-span-3">
                      <Label>Novo comentário</Label>
                      <textarea
                        className="input native"
                        rows={8}
                        placeholder="Escreva seu comentário… (Ctrl + Enter para enviar)"
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        onKeyDown={(e) => {
                          if (
                            (e.ctrlKey || e.metaKey) &&
                            e.key === "Enter" &&
                            commentText.trim()
                          ) {
                            postComment();
                          }
                        }}
                      />
                    </div>
                    <div className="col-span-1 flex items-end">
                      <Button
                        onClick={postComment}
                        disabled={postingComment || !commentText.trim()}
                      >
                        {postingComment ? "Enviando..." : "Enviar"}
                      </Button>
                    </div>
                  </div>

                  <div className="divider" />

                  {loadingComments ? (
                    <div>Carregando comentários…</div>
                  ) : comments.length === 0 ? (
                    <div className="empty-sub">Sem comentários.</div>
                  ) : (
                    <ul className="comments-list">
                      {comments.map((c) => (
                        <li key={c.ID_COMENTARIO} className="comment-item">
                          <div className="comment-header">
                            <b>{pickName(c) || getDevName(c.AUTOR_ID)}</b>
                            <span className="dot">•</span>
                            <span>{fmtDate(c.CREATED_AT)}</span>
                            {c.AUTOR_ID === Number(devId) && !c._optimistic && (
                              <Button
                                variant="ghost"
                                onClick={() => deleteComment(c)}
                                title="Remover"
                              >
                                Remover
                              </Button>
                            )}
                          </div>
                          <div className="comment-body">{c.TEXTO}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </AccordionSection>

              <AccordionSection
                id="anexos"
                title="Anexos"
                subtitle="Arquivos complementares, evidências e documentos técnicos"
                open={!!openAccordions.anexos}
                onToggle={toggleAccordion}
                badge={attachments.length ? `${attachments.length}` : null}
              >
                <div>
                  <div className="grid grid-4">
                    <div className="col-span-3">
                      <Label>Selecionar arquivo</Label>
                      <input
                        className="input native"
                        type="file"
                        onChange={(e) =>
                          setFileToUpload(e.target.files?.[0] || null)
                        }
                      />
                    </div>
                    <div className="col-span-1 flex items-end">
                      <Button
                        onClick={uploadAttachment}
                        disabled={!fileToUpload || uploadingAttachment}
                      >
                        {uploadingAttachment ? "Enviando..." : "Upload"}
                      </Button>
                    </div>
                  </div>

                  <div className="divider" />

                  {loadingAttachments ? (
                    <div>Carregando anexos…</div>
                  ) : attachments.length === 0 ? (
                    <div className="empty-sub">Sem anexos.</div>
                  ) : (
                    <ul className="attachments-list">
                      {attachments.map((a) => (
                        <li key={a.id} className="file-row">
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            className="file-name"
                            title={a.name}
                          >
                            {a.name}
                          </a>
                          {a.sizeHuman && (
                            <span className="file-meta">{a.sizeHuman}</span>
                          )}
                          <div className="file-actions">
                            <Button
                              variant="ghost"
                              onClick={() => downloadAttachment(manageDetail, a)}
                              disabled={a.__optimistic}
                            >
                              Baixar
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => deleteAttachment(a)}
                              disabled={a.__optimistic}
                            >
                              Remover
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </AccordionSection>

              <AccordionSection
                id="historico"
                title="Histórico / Auditoria"
                subtitle="Resumo operacional do projeto e últimos registros carregados"
                open={!!openAccordions.historico}
                onToggle={toggleAccordion}
              >
                <div className="workspace-timeline">
                  <div className="timeline-item">
                    <span className="timeline-dot" />
                    <div>
                      <b>Criação</b>
                      <p>
                        {fmtDate(manageDetail?.CREATED_AT)} por{" "}
                        {getDevName(manageDetail?.CRIADOR_ID)}
                      </p>
                    </div>
                  </div>
                  <div className="timeline-item">
                    <span className="timeline-dot" />
                    <div>
                      <b>Status atual</b>
                      <p>{STATUS_LABEL[normalizeStatus(manageDetail?.STATUS)] || "-"}</p>
                    </div>
                  </div>
                  <div className="timeline-item">
                    <span className="timeline-dot" />
                    <div>
                      <b>Previsão</b>
                      <p>{fmtDateOnly(manageDetail?.DT_PREVISTA)}</p>
                    </div>
                  </div>
                </div>
              </AccordionSection>

              <div className="workspace-footer-actions">
                <Button variant="secondary" onClick={() => setManageOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={saveManage} disabled={savingManage}>
                  {savingManage ? "Salvando…" : "Salvar alterações"}
                </Button>
              </div>
            </div>
          )}
        </PanelModal>

        <PanelModal
        open={!!operationalMode}
        size="md"
        title={
          operationalMode === "PAUSAR"
            ? "Pausar atividade"
            : operationalMode === "TROCAR"
            ? "Trocar atividade"
            : operationalMode === "INCIDENTE"
            ? "Registrar urgência não cadastrada"
            : "Operação"
        }
        onClose={closeOperational}
      >
        {({ firstFieldRef }) => (
          <div className="operational-form">
            {operationalMode === "PAUSAR" && (
              <>
                <div className="operational-context">
                  <b>
                    #{atividadeAtual?.ID_PROJETO} —{" "}
                    {atividadeAtual?.PROJETO_TITULO || "Projeto atual"}
                  </b>
                  <span>
                    Confirme a pausa somente quando o trabalho realmente não
                    puder continuar agora.
                  </span>
                </div>

                <div className="operational-summary-grid">
                  <div>
                    <span>Cronômetro</span>
                    <strong>Será encerrado</strong>
                  </div>
                  <div>
                    <span>Status do projeto</span>
                    <strong>Ficará pausado</strong>
                  </div>
                  <div>
                    <span>Histórico</span>
                    <strong>Será preservado</strong>
                  </div>
                </div>

                <Label>Motivo da pausa *</Label>
                <textarea
                  ref={firstFieldRef}
                  className="input native"
                  rows={5}
                  value={operationalForm.motivo}
                  onChange={(e) =>
                    setOperationalForm((prev) => ({
                      ...prev,
                      motivo: e.target.value,
                    }))
                  }
                  placeholder="Ex.: aguardando cliente, fim do expediente, bloqueio técnico..."
                />

                <div className="workspace-footer-actions">
                  <Button variant="secondary" onClick={closeOperational}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={confirmarPausa}
                    disabled={operationalSaving}
                  >
                    {operationalSaving ? "Pausando..." : "Confirmar pausa"}
                  </Button>
                </div>
              </>
            )}

            {operationalMode === "TROCAR" && (
              <>
                <div className="operational-context">
                  <b>
                    Origem: #{atividadeAtual?.ID_PROJETO} —{" "}
                    {atividadeAtual?.PROJETO_TITULO || "Projeto atual"}
                  </b>
                  <span>
                    A troca registra o atravesso: encerra a sessão atual como
                    interrupção e inicia automaticamente o projeto escolhido.
                  </span>
                </div>

                <div className="operational-summary-grid">
                  <div>
                    <span>Atividade atual</span>
                    <strong>Será interrompida</strong>
                  </div>
                  <div>
                    <span>Projeto de destino</span>
                    <strong>Será iniciado</strong>
                  </div>
                  <div>
                    <span>Auditoria</span>
                    <strong>Motivo registrado</strong>
                  </div>
                </div>

                <div className="grid grid-2">
                  <div>
                    <Label>Projeto que será iniciado *</Label>
                    <select
                      ref={firstFieldRef}
                      className="input native"
                      value={operationalForm.idProjetoDestino}
                      onChange={(e) => {
                        const value = e.target.value;
                        setOperationalForm((prev) => ({
                          ...prev,
                          idProjetoDestino: value,
                          idTarefaDestino: "",
                        }));
                        loadDestinationTasks(value);
                      }}
                    >
                      <option value="">Selecione...</option>
                      {rows
                        .filter(
                          (item) =>
                            Number(item.ID_PROJETO) !==
                            Number(atividadeAtual?.ID_PROJETO)
                        )
                        .map((item) => (
                          <option
                            key={item.ID_PROJETO}
                            value={item.ID_PROJETO}
                          >
                            #{item.ID_PROJETO} — {item.TITULO}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <Label>Tarefa de destino</Label>
                    <select
                      className="input native"
                      value={operationalForm.idTarefaDestino}
                      onChange={(e) =>
                        setOperationalForm((prev) => ({
                          ...prev,
                          idTarefaDestino: e.target.value,
                        }))
                      }
                      disabled={!operationalForm.idProjetoDestino}
                    >
                      <option value="">Trabalho geral do projeto</option>
                      {destinationTasks.map((tarefa) => (
                        <option
                          key={tarefa.ID_TAREFA}
                          value={tarefa.ID_TAREFA}
                        >
                          #{tarefa.ID_TAREFA} — {tarefa.TITULO}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label>Solicitante da interrupção</Label>
                    <Input
                      type="number"
                      min="1"
                      value={operationalForm.solicitanteId}
                      onChange={(e) =>
                        setOperationalForm((prev) => ({
                          ...prev,
                          solicitanteId: e.target.value,
                        }))
                      }
                      placeholder="ID do gestor ou solicitante"
                    />
                  </div>

                  <div>
                    <Label>Previsão de retorno</Label>
                    <Input
                      type="datetime-local"
                      value={operationalForm.previsaoRetorno}
                      onChange={(e) =>
                        setOperationalForm((prev) => ({
                          ...prev,
                          previsaoRetorno: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <Label>Motivo da interrupção *</Label>
                <textarea
                  className="input native"
                  rows={4}
                  value={operationalForm.motivo}
                  onChange={(e) =>
                    setOperationalForm((prev) => ({
                      ...prev,
                      motivo: e.target.value,
                    }))
                  }
                  placeholder="Descreva por que a prioridade atual foi interrompida."
                />

                <Label>Impacto</Label>
                <textarea
                  className="input native"
                  rows={3}
                  value={operationalForm.impacto}
                  onChange={(e) =>
                    setOperationalForm((prev) => ({
                      ...prev,
                      impacto: e.target.value,
                    }))
                  }
                  placeholder="Ex.: cliente sem emitir NFC-e, operação parada..."
                />

                <div className="workspace-footer-actions">
                  <Button variant="secondary" onClick={closeOperational}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={confirmarTroca}
                    disabled={operationalSaving}
                  >
                    {operationalSaving ? "Trocando..." : "Trocar e iniciar"}
                  </Button>
                </div>
              </>
            )}

            {operationalMode === "INCIDENTE" && (
              <>
                <div className="operational-context">
                  <b>Cadastro provisório de urgência</b>
                  <span>
                    A demanda será criada imediatamente, o trabalho atual será
                    interrompido quando existir e a nova sessão será iniciada.
                  </span>
                </div>

                <Label>Título *</Label>
                <Input
                  ref={firstFieldRef}
                  value={operationalForm.titulo}
                  onChange={(e) =>
                    setOperationalForm((prev) => ({
                      ...prev,
                      titulo: e.target.value,
                    }))
                  }
                  placeholder="Ex.: Cliente sem emitir NFC-e"
                />

                <Label>Descrição do problema *</Label>
                <textarea
                  className="input native"
                  rows={5}
                  value={operationalForm.descricao}
                  onChange={(e) =>
                    setOperationalForm((prev) => ({
                      ...prev,
                      descricao: e.target.value,
                    }))
                  }
                  placeholder="Informe o cenário, cliente e erro apresentado."
                />

                <div className="grid grid-2">
                  <div>
                    <Label>Prioridade</Label>
                    <select
                      className="input native"
                      value={operationalForm.prioridade}
                      onChange={(e) =>
                        setOperationalForm((prev) => ({
                          ...prev,
                          prioridade: e.target.value,
                        }))
                      }
                    >
                      <option value="CRITICA">CRÍTICA</option>
                      <option value="ALTA">ALTA</option>
                      <option value="MEDIA">MÉDIA</option>
                      <option value="BAIXA">BAIXA</option>
                    </select>
                  </div>

                  <div>
                    <Label>Solicitante</Label>
                    <Input
                      type="number"
                      min="1"
                      value={operationalForm.solicitanteId}
                      onChange={(e) =>
                        setOperationalForm((prev) => ({
                          ...prev,
                          solicitanteId: e.target.value,
                        }))
                      }
                      placeholder="ID do solicitante"
                    />
                  </div>
                </div>

                <Label>Impacto</Label>
                <textarea
                  className="input native"
                  rows={3}
                  value={operationalForm.impacto}
                  onChange={(e) =>
                    setOperationalForm((prev) => ({
                      ...prev,
                      impacto: e.target.value,
                    }))
                  }
                  placeholder="Quem está parado e qual o impacto operacional?"
                />

                <div className="workspace-footer-actions">
                  <Button variant="secondary" onClick={closeOperational}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={confirmarIncidente}
                    disabled={operationalSaving}
                  >
                    {operationalSaving
                      ? "Registrando..."
                      : "Criar incidente e iniciar"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </PanelModal>

        {commentAsk.open && (
          <div className="comment-modal-layer">
            <CommentModal
              open={commentAsk.open}
              title={commentAsk.title}
              subtitle={commentAsk.subtitle}
              onCancel={closeCommentModal}
              onConfirm={(txt) => commentAsk.onConfirm?.(txt)}
            />
          </div>
        )}
      </div>
    </MainLayout>
  );
}
